import { Injectable } from '@angular/core';
import { find, findIndex, sum, round } from 'lodash';
import { DataService } from './data.service';

@Injectable()
export class CalculationService {
  services: any;

  constructor(private dataService: DataService) { }

  mathCeil(data) {
    return Math.ceil(data);
  }

  calculateMarkUpPrice(cost: number, markup: number = 0) {
    const value = {
      cost: this.roundOff(cost),
      markup: this.roundOff(markup),
    };

    return this.roundOff((1 + value.markup / 100) * value.cost);
  }

  calculateMarkUpUsingPricing(cost: number, pricing: any) {
    const itemsLength = pricing && pricing.items && pricing.items.length;
    let markup = 0;

    if (itemsLength) {
      for (let i = 0; i < itemsLength; i++) {
        const item = pricing.items[i];

        if (cost >= item.min && cost <= item.max) {
          markup = item.markup;
          break;
        }
      }
    }

    return this.calculateMarkUpPrice(cost, markup);
  }
  /** calculating the remaining balanc of order*/
  calculateRemainingBalance(order) {
    const { total, cash, cheque, card, creditPayment, thirdParty } = order;
    const _sum = sum([this.roundOff(cash), this.roundOff(cheque), this.roundOff(card), this.roundOff(creditPayment), this.roundOff(thirdParty)]);

    if (_sum) {
      const { grand } = total;
      return this.roundOff(grand - _sum);
    } else {
      try {
        if (order.total && order.total.hasOwnProperty && order.total.hasOwnProperty('unpaid')) {
          return this.roundOff((order.total.unpaid || 0).toFixed(2));
        } else {
          return this.roundOff((order.total || 0).toFixed(2) - (order.paid || 0).toFixed(2));
        }
      } catch (err) {
        return 0;
      }
    }

    return 0;
  }

  convertMileageIntoUnit(mileage: number, value: string) {
    return mileage;
  }

  calculateOutTheDoorPrice(items: any[] = []): any {
    let obj: any = {
      serviceTotalPrice: 0,
      serviceTotalCost: 0,
      serviceTotalTaxAbleAmt: 0,
    };
    items.forEach((item: any) => {
      if (item.checked) {
        obj.serviceTotalCost += item.cost * (item?.count || 0);
        if (item.type === 'bundle') {
          obj.serviceTotalPrice += item.flatRate;
        } else {
          obj.serviceTotalPrice += item.price * (item?.count || 0);
          if (item?.isTaxable) {
            obj.serviceTotalTaxAbleAmt += item.price * (item?.count || 0);
          }
        }
      }
    });
    return obj;
  }

  cannedService(service: any, isQTQAddOnEnable = false) {
    const serviceTotal = {
      part: 0,
      labor: 0,
      other: 0,
      sublet: 0,
      tire: 0,
      cost: 0,
      total: 0,
      hazmatFee: 0,
    };

    service.items.forEach((serviceItem: any) => {
      if (serviceItem.status) {
        if (serviceItem.subTotal >= 0 && serviceItem.type) {
          if (serviceItem.type === 'bundle' && serviceItem.subItems && serviceItem.subItems.length) {
            serviceItem.subItems.forEach((subItem: any) => {
              serviceTotal[subItem.type] += subItem.subTotal;
            });
          } else {
            serviceTotal[serviceItem.type] += serviceItem.subTotal;
          }
        }

        if (service.qtqAddOn && isQTQAddOnEnable) {
          if (serviceItem.checked) serviceTotal.total += serviceItem.subTotal;
        } else {
          serviceTotal.total += serviceItem.subTotal;
        }

        if (['part', 'labor', 'tire', 'hazmatFee'].includes(serviceItem.type)) {
          serviceTotal.cost += serviceItem.cost;
        }
      }
    });

    return serviceTotal;
  }

  calculateOrderItemDiscount({ title, discount, price, count }) {
    if (discount) {
      let discountAmt = 0;

      if (discount.type === 'percentage') {
        discountAmt = this.roundOff((discount.value / 100) * (price * count));
      } else if (discount.type === 'value') {
        discountAmt = this.roundOff(discount.value);
      }

      return discountAmt;
    }

    return 0;
  }

  calculatingMotorPartCost(price: number, pricing: any) {
    if (pricing && pricing.items && pricing.items.length) {
      for (let i = 0; i < pricing.items.length; i++) {
        const { min, max, markup } = pricing.items[i];
        const cost = (price * 100) / (100 + markup);

        if (cost >= min && cost <= max) {
          return cost;
        }
      }
    }

    return price;
  }

  calculateLaborMarkupTime(years: number, hours: number, matrix: any) {
    if (matrix && matrix.items && matrix.items.length) {
      for (let i = 0; i < matrix.items.length; i++) {
        const { min, max, markup } = matrix.items[i];

        if (years >= min && years <= max) {
          return { markup, value: this.roundOff(hours + (hours * markup) / 100) };
        }
      }
    }

    return { markup: 0, value: this.roundOff(hours) };
  }

  calculateOrderItemTax({ total, taxValue, roundOffTax }) {
    if (roundOffTax) {
      return this.roundOff((taxValue / 100) * total);
    } else {
      return (taxValue / 100) * total;
    }
  }

  calculateOrderItemTaxes({ total, taxRates = [], roundOffTax, itemType = '' }) {
    let result = {};
    let taxAmt = 0;

    taxRates.forEach(({ taxId, salesTaxRate }) => {
      if (roundOffTax) {
        const amt = this.roundOff((salesTaxRate / 100) * total);
        result[taxId] = amt * (itemType === 'discount' ? -1 : 1);
        taxAmt = this.roundOff(taxAmt + amt);
      } else {
        const amt = (salesTaxRate / 100) * total;
        result[taxId] = amt * (itemType === 'discount' ? -1 : 1);
        taxAmt += amt;
      }
    });

    return { taxAmt, ...result };
  }

  calculateTaxRatesAmount({ taxRates, taxAmounts, roundOffTax }: any) {
    return taxRates.map((rate: any) => {
      const { taxId, taxAmount } = rate;

      if (taxAmounts[taxId]) {
        if (roundOffTax) {
          rate.taxAmount = this.roundOff(taxAmount + taxAmounts[taxId]);
        } else {
          rate.taxAmount = taxAmount + taxAmounts[taxId];
        }
      }

      return rate;
    });
  }

  calculateSalePriceAndProfitMargin(cost: number, markup: number): number {
    const itemcost = cost;
    const itemMarkup = markup;

    const maxCost = this.roundOff(itemcost);
    const markupPercent = this.roundOff(itemMarkup);
    const salesPrice = this.roundOff(maxCost + (maxCost * markupPercent) / 100);
    return salesPrice;
  }

  calculateProfiltMargin(markup: number): number {
    const profiltMarginPercentage = this.roundOff(markup / (1 + markup / 100));
    return profiltMarginPercentage;
  }

  calculateMarkup(profiltMargin: number): number {
    if (profiltMargin / 100 === 0) {
      return 0;
    }
    const markupPercentage = this.roundOff(profiltMargin / (1 - profiltMargin / 100));
    return markupPercentage;
  }

  estimateServices(estimate: any, tax: any, discount: any) {
    if (estimate.isQuickbooksCalCompatibiltyOn) {
      return this.estimateCalQuickbookCompatible(estimate, tax, discount);
    }

    if (estimate.shopFeeBeforeDiscount) {
      return this.estimateServicesUpdated(estimate, tax, discount);
    }
    // Tax rate value
    let taxValue = 0;

    if (tax && tax.tax) {
      if (tax.tax.value) {
        taxValue = tax.tax.value;
      } else if (tax.tax.salesTaxRate) {
        taxValue = tax.tax.salesTaxRate;
      }
    }

    // Tax Value object for recoard in estimate
    estimate.taxValues = {
      part: tax.parts, // For now both keys are required
      parts: tax.parts, // For now both keys are required
      labor: tax.labor,
      tire: tax.tire,
      tireStorage: tax.tireStorage,
      other: tax.other,
      sublet: tax.sublet,
      taxRate: taxValue,
      preDiscountTax: tax && tax.preDiscountTax,
    };

    // Estimate total default object
    estimate.total = {
      parts: 0,
      labor: 0,
      tire: 0,
      other: 0,
      total: 0,
      afterDiscount: 0,
      afterTax: 0,
      grand: 0,
      hazmatFee: 0,
      passThroughTax: 0,
      itemDiscountedAmount: 0,
      discountedAmount: 0,
      taxedAmount: 0,
      discount: discount,
      shopFee: 0,
      shopSupplyFee: (estimate.total && estimate.total.shopSupplyFee) || {},
      shopFeePercent: 0,
      paid: (estimate.total && estimate.total.paid) || 0,
      unpaid: (estimate.total && estimate.total.unpaid) || 0,
      tax: {
        type: 'percentage',
        value: taxValue,
        qboId: tax && tax.tax && tax.tax.qboId,
        name: tax && tax.tax && tax.tax.name ? tax.tax.name : '',
      },
    };

    estimate.totalCost = {
      part: 0,
      labor: 0,
      tire: 0,
      other: 0,
      sublet: 0,
      hazmatFee: 0,
      passThroughTax: 0,
    };
    estimate.billableHours = 0;
    // Total Taxable Amount
    let taxableAmount = 0;
    let NonTaxableAmount = 0;

    // Total Discount Amount for Taxable Items
    let taxableItemDiscountAmount = 0;

    // Include services which are marked as Include In Invoice
    const included = estimate.services.filter((s) => s.includeInInvoice === true);

    const notIncluded = estimate.services.filter((s) => !s.includeInInvoice);

    notIncluded.forEach((e) => {
      // Service total default object
      e.serviceTotal.part = 0;
      e.serviceTotal.labor = 0;
      e.serviceTotal.tire = 0;
      e.serviceTotal.other = 0;
      e.serviceTotal.sublet = 0;
      e.serviceTotal.hazmatFee = 0;
      e.serviceTotal.passThroughTax = 0;
      e.billableHours = 0;
      e.serviceTotal.serviceDiscountAmt = 0;
      e.serviceTotal.serviceTaxableAmt = 0;
      e.serviceTotal.serviceNonTaxableAmt = 0;
      e.totalCost = {
        part: 0,
        labor: 0,
        tire: 0,
        other: 0,
        sublet: 0,
        hazmatFee: 0,
        passThroughTax: 0,
      };

      // Total for labor, part, other
      e.items.forEach((t) => {
        if (t.type === 'labor') {
          e.totalCost[t.type] += this.roundOff(t.cost);
          estimate.totalCost[t.type] += this.roundOff(t.cost);
        } else {
          if (t.type === 'hazmatFee' && t.isPassThroughTax) {
            e.totalCost['passThroughTax'] += this.roundOff(t.cost * t.count);
            estimate.totalCost['passThroughTax'] += this.roundOff(t.cost * t.count);
          } else {
            e.totalCost[t.type] += this.roundOff(t.cost * t.count);
            estimate.totalCost[t.type] += this.roundOff(t.cost * t.count);
          }
        }

        t.price = this.roundOff(t.price);
        t.count = this.roundOff(t.count);
        t.subTotal = this.roundOff(t.price * t.count);

        if (t.price >= 0) {
          // Calculating discount amount for the item
          const itemDiscount = this.calculateOrderItemDiscount(t);

          if (estimate.taxValues[t.type] && t.isTaxable) {
            e.serviceTotal.serviceTaxableAmt += this.roundOff(t.subTotal - itemDiscount);
            taxableItemDiscountAmount += itemDiscount;
          } else {
            e.serviceTotal.serviceNonTaxableAmt += this.roundOff(t.subTotal - itemDiscount);
          }

          e.serviceTotal.serviceDiscountAmt += itemDiscount;

          if (!t.isPassThroughTax) {
            e.serviceTotal['passThroughTax'] += this.roundOff(t.subTotal);
          } else {
            e.serviceTotal[t.type] += this.roundOff(t.subTotal);
          }

          if (t.type === 'labor' && t.count >= 0) {
            e.billableHours += t.count;
            estimate.billableHours += t.count;
          }
        }
      });

      // Subtotal for all services labor, parts, other  without tax and discount
      e.serviceTotal.total = this.roundOff(e.serviceTotal.part + e.serviceTotal.labor + e.serviceTotal.tire + e.serviceTotal.other + e.serviceTotal.sublet);
    });

    included.forEach((e) => {
      // Service total default object
      e.serviceTotal.part = 0;
      e.serviceTotal.labor = 0;
      e.serviceTotal.tire = 0;
      e.serviceTotal.other = 0;
      e.serviceTotal.sublet = 0;
      e.serviceTotal.hazmatFee = 0;
      e.serviceTotal.passThroughTax = 0;
      e.billableHours = 0;
      e.serviceTotal.serviceDiscountAmt = 0;
      e.serviceTotal.serviceTaxableAmt = 0;
      e.serviceTotal.serviceNonTaxableAmt = 0;
      e.totalCost = {
        part: 0,
        labor: 0,
        tire: 0,
        other: 0,
        sublet: 0,
        hazmatFee: 0,
        passThroughTax: 0,
      };

      // Total for labor, part, other
      e.items.forEach((t) => {
        if (t.type === 'labor') {
          e.totalCost[t.type] += this.roundOff(t.cost);
          estimate.totalCost[t.type] += this.roundOff(t.cost);
        } else {
          if (t.type === 'hazmatFee' && t.isPassThroughTax) {
            e.totalCost['passThroughTax'] += this.roundOff(t.cost * t.count);
            estimate.totalCost['passThroughTax'] += this.roundOff(t.cost * t.count);
          } else {
            e.totalCost[t.type] += this.roundOff(t.cost * t.count);
            estimate.totalCost[t.type] += this.roundOff(t.cost * t.count);
          }
        }

        t.price = this.roundOff(t.price);
        t.count = this.roundOff(t.count);
        t.subTotal = this.roundOff(t.price * t.count);

        if (t.price >= 0) {
          // Calculating discount amount for the item
          const itemDiscount = this.calculateOrderItemDiscount(t);

          if (estimate.taxValues[t.type] && t.isTaxable) {
            e.serviceTotal.serviceTaxableAmt += this.roundOff(t.subTotal - itemDiscount);
            taxableItemDiscountAmount += itemDiscount;
          } else {
            e.serviceTotal.serviceNonTaxableAmt += this.roundOff(t.subTotal - itemDiscount);
          }

          e.serviceTotal.serviceDiscountAmt += itemDiscount;

          if (!t.isPassThroughTax) {
            e.serviceTotal['passThroughTax'] += this.roundOff(t.subTotal);
          } else {
            e.serviceTotal[t.type] += this.roundOff(t.subTotal);
          }

          if (t.type === 'labor' && t.count >= 0) {
            e.billableHours += t.count;
            estimate.billableHours += t.count;
          }
        }
      });

      // Subtotal for all services labor, parts, tire, other,sublet without tax and discount
      e.serviceTotal.total = this.roundOff(e.serviceTotal.part + e.serviceTotal.labor + e.serviceTotal.tire + e.serviceTotal.other + e.serviceTotal.sublet);

      // Individual Total for all services labor, parts, tire, other
      estimate.total.labor += e.serviceTotal.labor;
      estimate.total.parts += e.serviceTotal.part;
      estimate.total.tire += e.serviceTotal.tire;
      estimate.total.other += e.serviceTotal.other;
      estimate.total.sublet += e.serviceTotal.sublet;
      estimate.total.hazmatFee += e.serviceTotal.hazmatFee;
      estimate.total.passThroughTax += estimate.invoiceType ? -1 * e.serviceTotal.passThroughTax : e.serviceTotal.passThroughTax;

      // Total service-discounts
      estimate.total.itemDiscountedAmount += this.roundOff(e.serviceTotal.serviceDiscountAmt);

      // Total taxable amount
      taxableAmount += this.roundOff(e.serviceTotal.serviceTaxableAmt);
      NonTaxableAmount += this.roundOff(e.serviceTotal.serviceNonTaxableAmt);
    });

    // Subtotal
    estimate.total.total = this.roundOff(estimate.total.labor + estimate.total.parts + estimate.total.other + estimate.total.sublet + estimate.total.tire);

    // Calculating discount after subtracting the service discount
    if (discount.type === 'percentage') {
      estimate.total.discountedAmount = this.roundOff((estimate.total.total - estimate.total.itemDiscountedAmount) * (discount.value / 100));
    } else if (discount.type === 'value') {
      estimate.total.discountedAmount = discount.value;
    }

    // Calculating total after subtracting order + service discount
    estimate.total.afterDiscount = this.roundOff(estimate.total.total - (estimate.total.itemDiscountedAmount + estimate.total.discountedAmount));

    // Calculating shop fees from the after discount and in case of shop fees in percent apply cap in case of discount is greater then cutoff value
    if (estimate.isShopFeeEnabled && estimate.total.shopSupplyFee && estimate.total.shopSupplyFee.value) {
      if (estimate.total.shopSupplyFee.type === 'percentage') {
        estimate.total.shopFee = this.roundOff(estimate.total.afterDiscount * ((estimate.total.shopSupplyFee.value || 0) / 100));

        if (estimate.total.afterDiscount && estimate.total.shopSupplyFee.cap && estimate.total.shopFee > estimate.total.shopSupplyFee.cap) {
          estimate.total.shopFee = estimate.total.shopSupplyFee.cap;
          estimate.total.shopFeePercent = ((estimate.total.shopFee || 0) * 100) / estimate.total.afterDiscount;
        } else {
          estimate.total.shopFeePercent = estimate.total.shopSupplyFee.value || 0;
        }
      } else {
        estimate.total.shopFee = this.roundOff(estimate.total.shopSupplyFee.value || 0);

        if (estimate.total.afterDiscount) {
          estimate.total.shopFeePercent = ((estimate.total.shopFee || 0) * 100) / estimate.total.afterDiscount;
        }
      }
    }

    estimate.total.shopFeePercent = this.roundOff(estimate.total.shopFeePercent);

    estimate.total.taxableAmount = this.roundOff(taxableAmount);
    estimate.total.nonTaxableAmount = this.roundOff(NonTaxableAmount);

    if (tax && !tax.preDiscountTax) {
      const discApplicableOnTax = estimate.total.taxableAmount * (estimate.total.discountedAmount / (estimate.total.taxableAmount + estimate.total.nonTaxableAmount));
      const discApplicableOnNonTax = estimate.total.nonTaxableAmount * (estimate.total.discountedAmount / (estimate.total.taxableAmount + estimate.total.nonTaxableAmount));

      estimate.total.taxableAmount = this.roundOff(estimate.total.taxableAmount + estimate.total.shopFee - discApplicableOnTax);
      estimate.total.nonTaxableAmount -= this.roundOff(discApplicableOnNonTax);

      // Calculate Tax Post-discount tax
      estimate.total.taxedAmount = this.roundOff((taxValue / 100) * estimate.total.taxableAmount);

      estimate.total.afterDiscount += estimate.total.shopFee || 0;
      estimate.total.afterTax = this.roundOff(estimate.total.afterDiscount + estimate.total.taxedAmount);

      estimate.total.grand = this.roundOff(estimate.total.afterTax + (estimate.total.passThroughTax || 0));
    }

    return estimate;
  }

  estimateServicesUpdated(estimate, tax, discount) {
    // Tax rate value
    let taxValue = 0;

    if (tax && tax.tax) {
      if (tax.tax.value) {
        taxValue = tax.tax.value;
      } else if (tax.tax.salesTaxRate) {
        taxValue = tax.tax.salesTaxRate;
      }
    }

    // Tax Value object for recoard in estimate
    estimate.taxValues = {
      taxRate: taxValue,
      preDiscountTax: tax && tax.preDiscountTax,
      part: tax.parts, // For now both keys are required
      parts: tax.parts, // For now both keys are required
      labor: tax.labor,
      tire: tax.tire,
      tireStorage: tax.tireStorage,
      hazmatFee: tax.hazmatFee,
      other: tax.other,
    };

    // Estimate total default object
    estimate.total = {
      parts: 0,
      labor: 0,
      tire: 0,
      tireStorage: 0,
      hazmatFee: 0,
      passThroughTax: 0,
      other: 0,
      total: 0,
      itemTypeDiscount: {
        part: 0,
        labor: 0,
        tire: 0,
        tireStorage: 0,
        hazmatFee: 0,
        other: 0,
      },
      afterDiscount: 0,
      afterTax: 0,
      grand: 0,
      itemDiscountedAmount: 0,
      discountedAmount: 0,
      taxedAmount: 0,
      discount: discount,
      shopFee: 0,
      shopSupplyFee: (estimate.total && estimate.total.shopSupplyFee) || {},
      shopFeePercent: 0,
      paid: (estimate.total && estimate.total.paid) || 0,
      unpaid: (estimate.total && estimate.total.unpaid) || 0,
      tax: {
        type: 'percentage',
        value: taxValue,
        qboId: tax && tax.tax && tax.tax.qboId,
        name: tax && tax.tax && tax.tax.name ? tax.tax.name : '',
      },
    };

    estimate.totalCost = {
      part: 0,
      labor: 0,
      tire: 0,
      tireStorage: 0,
      hazmatFee: 0,
      passThroughTax: 0,
      other: 0,
    };
    estimate.billableHours = 0;
    // Total Taxable Amount
    let taxableAmount = 0;
    let NonTaxableAmount = 0;

    // Total Discount Amount for Taxable Items
    let taxableItemDiscountAmount = 0;

    // Include services which are marked as Include In Invoice
    const included = estimate.services.filter((s) => s.includeInInvoice === true);

    const notIncluded = estimate.services.filter((s) => !s.includeInInvoice);

    notIncluded.forEach((e) => {
      // Service total default object
      e.serviceTotal.part = 0;
      e.serviceTotal.labor = 0;
      e.serviceTotal.tire = 0;
      e.serviceTotal.hazmatFee = 0;
      e.serviceTotal.other = 0;
      e.billableHours = 0;
      e.serviceTotal.serviceDiscountAmt = 0;
      e.serviceTotal.serviceTaxableAmt = 0;
      e.serviceTotal.serviceNonTaxableAmt = 0;
      e.totalCost = {
        part: 0,
        labor: 0,
        tire: 0,
        hazmatFee: 0,
        other: 0,
      };

      // Total for labor, part, other
      e.items.forEach((t) => {
        if (t.type === 'labor') {
          e.totalCost[t.type] += this.roundOff(t.cost);
          // estimate.totalCost[t.type] += roundOff(t.cost);
        } else {
          if (t.isPassThroughTax) {
            e.totalCost['passThroughTax'] += this.roundOff(t.cost * t.count);
          } else {
            e.totalCost[t.type] += this.roundOff(t.cost * t.count);
          }
          // estimate.totalCost[t.type] += roundOff(t.cost * t.count);
        }

        t.price = this.roundOff(t.price);
        t.count = this.roundOff(t.count);
        t.subTotal = this.roundOff(t.price * t.count);

        if (t.price >= 0) {
          // Calculating discount amount for the item
          const itemDiscount = this.calculateOrderItemDiscount(t);

          if (estimate.taxValues[t.type] && t.isTaxable) {
            e.serviceTotal.serviceTaxableAmt += this.roundOff(t.subTotal - itemDiscount);
            taxableItemDiscountAmount += itemDiscount;
          } else {
            e.serviceTotal.serviceNonTaxableAmt += this.roundOff(t.subTotal - itemDiscount);
          }

          e.serviceTotal.serviceDiscountAmt += itemDiscount;
          if (t.isPassThroughTax) {
            e.serviceTotal['passThroughTax'] += this.roundOff(t.subTotal);
          } else {
            e.serviceTotal[t.type] += this.roundOff(t.subTotal);
          }

          if (t.type === 'labor' && t.count >= 0) {
            e.billableHours += t.count;
            estimate.billableHours += t.count;
          }
        }
      });

      // Subtotal for all services labor, parts, other  without tax and discount
      e.serviceTotal.total = this.roundOff(e.serviceTotal.part + e.serviceTotal.labor + e.serviceTotal.tire + e.serviceTotal.other);
    });

    included.forEach((e) => {
      // Service total default object
      e.serviceTotal.part = 0;
      e.serviceTotal.labor = 0;
      e.serviceTotal.tire = 0;
      e.serviceTotal.hazmatFee = 0;
      e.serviceTotal.passThroughTax = 0;
      e.serviceTotal.other = 0;
      e.serviceTotal.itemTypeDiscount = {
        part: 0,
        labor: 0,
        tire: 0,
        hazmatFee: 0,
        other: 0,
      };
      e.billableHours = 0;
      e.serviceTotal.serviceDiscountAmt = 0;
      e.serviceTotal.serviceTaxableAmt = 0;
      e.serviceTotal.serviceNonTaxableAmt = 0;
      e.totalCost = {
        part: 0,
        labor: 0,
        tire: 0,
        hazmatFee: 0,
        passThroughTax: 0,
        other: 0,
      };

      // Total for labor, part, other
      e.items.forEach((t) => {
        if (t.type === 'labor') {
          e.totalCost[t.type] += this.roundOff(t.cost);
          estimate.totalCost[t.type] += this.roundOff(t.cost);
        } else {
          if (t.isPassThroughTax) {
            e.totalCost['passThroughTax'] += this.roundOff(t.cost * t.count);
            estimate.totalCost['passThroughTax'] += this.roundOff(t.cost * t.count);
          } else {
            e.totalCost[t.type] += this.roundOff(t.cost * t.count);
            estimate.totalCost[t.type] += this.roundOff(t.cost * t.count);
          }
        }

        t.price = this.roundOff(t.price);
        t.count = this.roundOff(t.count);
        t.subTotal = this.roundOff(t.price * t.count);

        if (t.price >= 0) {
          // Calculating discount amount for the item
          const itemDiscount = this.calculateOrderItemDiscount(t);
          e.serviceTotal.itemTypeDiscount[t.type] += itemDiscount;
          estimate.total.itemTypeDiscount[t.type] += itemDiscount;

          if (estimate.taxValues[t.type] && t.isTaxable) {
            e.serviceTotal.serviceTaxableAmt += this.roundOff(t.subTotal);
            taxableItemDiscountAmount += itemDiscount;
          } else {
            e.serviceTotal.serviceNonTaxableAmt += this.roundOff(t.subTotal);
          }

          e.serviceTotal.serviceDiscountAmt += itemDiscount;
          if (t.isPassThroughTax) {
            e.serviceTotal['passThroughTax'] += this.roundOff(t.subTotal);
          } else {
            e.serviceTotal[t.type] += this.roundOff(t.subTotal);
          }

          if (t.type === 'labor' && t.count >= 0) {
            e.billableHours += t.count;
            estimate.billableHours += t.count;
          }
        }
      });

      // Subtotal for all services labor, parts, tire, other without tax and discount
      e.serviceTotal.total = this.roundOff(e.serviceTotal.part + e.serviceTotal.labor + e.serviceTotal.tire + e.serviceTotal.hazmatFee + e.serviceTotal.other);

      // Individual Total for all services labor, parts, tire, other
      estimate.total.labor += e.serviceTotal.labor;
      estimate.total.parts += e.serviceTotal.part;
      estimate.total.tire += e.serviceTotal.tire;
      estimate.total.hazmatFee += e.serviceTotal.hazmatFee;
      estimate.total.passThroughTax += estimate.invoiceType ? -1 * e.serviceTotal.passThroughTax : e.serviceTotal.passThroughTax;
      estimate.total.other += e.serviceTotal.other;

      // Total service-discounts
      estimate.total.itemDiscountedAmount += this.roundOff(e.serviceTotal.serviceDiscountAmt);

      // Total taxable amount
      taxableAmount += this.roundOff(e.serviceTotal.serviceTaxableAmt);
      NonTaxableAmount += this.roundOff(e.serviceTotal.serviceNonTaxableAmt);
    });

    // Subtotal
    estimate.total.total = this.roundOff(estimate.total.labor + estimate.total.parts + estimate.total.other + estimate.total.tire);

    // Calculating total after subtracting order + service discount
    estimate.total.afterDiscount = this.roundOff(estimate.total.total - estimate.total.itemDiscountedAmount);

    // Calculating shop fees from the after discount and in case of shop fees in percent apply cap in case of discount is greater then cutoff value\
    if (estimate.isShopFeeEnabled && estimate.total.shopSupplyFee && estimate.total.shopSupplyFee.value) {
      if (estimate.total.shopSupplyFee.type === 'percentage') {
        estimate.total.shopFee = this.roundOff(estimate.total.afterDiscount * ((estimate.total.shopSupplyFee.value || 0) / 100));

        if (estimate.total.afterDiscount && estimate.total.shopSupplyFee.cap && estimate.total.shopFee > estimate.total.shopSupplyFee.cap) {
          estimate.total.shopFee = estimate.total.shopSupplyFee.cap;
          estimate.total.shopFeePercent = ((estimate.total.shopFee || 0) * 100) / estimate.total.afterDiscount;
        } else {
          estimate.total.shopFeePercent = estimate.total.shopSupplyFee.value || 0;
        }
      } else {
        estimate.total.shopFee = this.roundOff(estimate.total.shopSupplyFee.value || 0);

        if (estimate.total.afterDiscount) {
          estimate.total.shopFeePercent = ((estimate.total.shopFee || 0) * 100) / estimate.total.afterDiscount;
        }
      }
    }

    estimate.total.shopFeePercent = this.roundOff(estimate.total.shopFeePercent);

    estimate.total.afterDiscount += estimate.total.shopFee;

    // Calculating discount after subtracting the service discount
    if (discount.type === 'percentage') {
      estimate.total.discountedAmount = this.roundOff(estimate.total.afterDiscount * (discount.value / 100));
    } else if (discount.type === 'value') {
      estimate.total.discountedAmount = discount.value;
    }

    estimate.total.afterDiscount -= estimate.total.discountedAmount;
    estimate.total.taxableAmount = this.roundOff(taxableAmount + estimate.total.shopFee);
    estimate.total.nonTaxableAmount = this.roundOff(NonTaxableAmount);

    if (tax && !tax.preDiscountTax) {
      const discApplicableOnTax =
        estimate.total.taxableAmount * ((estimate.total.discountedAmount + estimate.total.itemDiscountedAmount) / (estimate.total.taxableAmount + estimate.total.nonTaxableAmount));
      const discApplicableOnNonTax =
        estimate.total.nonTaxableAmount * ((estimate.total.discountedAmount + estimate.total.itemDiscountedAmount) / (estimate.total.taxableAmount + estimate.total.nonTaxableAmount));

      estimate.total.taxableAmount -= this.roundOff(discApplicableOnTax);
      estimate.total.nonTaxableAmount -= this.roundOff(discApplicableOnNonTax);

      // Calculate Tax Post-discount tax
      estimate.total.taxedAmount = this.roundOff((taxValue / 100) * estimate.total.taxableAmount);

      // estimate.total.afterDiscount += estimate.total.shopFee || 0;
      estimate.total.afterTax = this.roundOff(estimate.total.afterDiscount + estimate.total.taxedAmount);

      estimate.total.grand = this.roundOff(estimate.total.afterTax) + this.roundOff(estimate.total.passThroughTax || 0);
    }

    return estimate;
  }

  calculateOrderItemPropDiscount = ({ roundOffTax = true, total, netSubtotal, discountedAmount }) => {
    if (!total) return 0;

    if (roundOffTax) {
      return this.roundOff(netSubtotal * (discountedAmount / total));
    } else {
      return netSubtotal * (discountedAmount / total);
    }
  };

  adjustROAmount(estimate: any, tax: any, expectedValue: number, options: any = {}) {
    let order = JSON.parse(JSON.stringify(estimate));
    // new discount = old discount + difference
    // difference = invoice amount - expected amount

    expectedValue = this.roundOff(expectedValue);

    if (expectedValue < order.total.grand) {
      return this.roRoundDownCalculation(order, tax, expectedValue, options);
    } else {
      order.isShopFeeEnabled = true;
      return this.roRoundUpCalculation(order, tax, expectedValue, options);
    }
  }

  roRoundDownCalculation(order: any, tax: any, expectedValue: number, options: any = {}) {
    let _order = JSON.parse(JSON.stringify(order));
    let difference = this.roundOff(order.total.grand - expectedValue);
    let actualDiscountAmount = 0;
    let updatedDiscount = 0;
    let discountItem: any = {};
    let discountItemIndex = -1;

    if (_order.isDiscountItemEnabled) {
      _order.total.orderDiscountItems = _order.total.orderDiscountItems || [];
      // Fetching discount item from the selected options
      discountItemIndex = findIndex(_order.total.orderDiscountItems, { isDefault: true });

      // If default discount is not already added to the RO fetch it from the order option
      if (discountItemIndex === -1) {
        const defaultDiscount = find((options && options.orderDiscounts) || [], { isDefault: true });

        if (discountItem && defaultDiscount) {
          _order.total.orderDiscountItems.push({
            discountId: defaultDiscount._id,
            taxable: false,
            isDefault: defaultDiscount.isDefault,
            value: defaultDiscount.discountValue,
            cap: defaultDiscount.discountCap,
            type: defaultDiscount.discountType,
            discountCalcValue: 0,
            discountCalcPercent: 0,
            active: defaultDiscount.status,
            qboId: defaultDiscount.qboId,
            name: defaultDiscount.title,
          });
          discountItemIndex = findIndex(_order.total.orderDiscountItems, { isDefault: true });
        }
      }

      if (discountItemIndex !== -1) {
        discountItem = { ..._order.total.orderDiscountItems[discountItemIndex] };
        updatedDiscount = discountItem.discountCalcValue + difference;
        actualDiscountAmount = discountItem.discountCalcValue;
        _order.total.orderDiscountItems[discountItemIndex].type = 'value';
      } else {
        return null;
      }
    } else {
      updatedDiscount = _order.total.discountedAmount + difference;
      discountItem = _order.total.discount;
      actualDiscountAmount = _order.total.discountedAmount;
    }

    let iteration = 0;
    let actualDiscount = { ...(discountItem || {}) };
    let actualOrderValue = _order.total.grand;

    while (difference && iteration < 20) {
      _order = this.estimateCalQuickbookCompatible(_order, tax, _order.total.discount);

      difference = this.roundOff(_order.total.grand - expectedValue);

      if (_order.isDiscountItemEnabled) {
        updatedDiscount = this.roundOff(_order.total.orderDiscountItems[discountItemIndex].discountCalcValue + difference);
        _order.total.orderDiscountItems[discountItemIndex].value = this.roundOff(_order.total.orderDiscountItems[discountItemIndex].discountCalcValue + difference);
      } else {
        updatedDiscount = this.roundOff(_order.total.discountedAmount + difference);
        _order.total.discount.type = 'value';
        _order.total.discount.value = updatedDiscount;
      }
      iteration++;
    }

    if (_order) {
      _order.adjustments = {
        isRoundDown: true,
        adjustedValue: expectedValue,
        actualDiscount,
        actualShopSupplies: {},
        isApplied: true,
        actualOrderValue,
        actualDiscountAmount: actualDiscountAmount,
      };
    }

    // if (_order.invoiceType && _order.isDiscountItemEnabled) {
    //   const defaultOrderDiscountIndex = findIndex(_order.total.orderDiscountItems, { isDefault: true });

    //   if (defaultOrderDiscountIndex !== -1) {
    //     const discountApplicableAmount = subtract(sum([_order.total.total, _order.total.totalFee]), sum([_order.total.itemLevelDiscount, _order.total.serviceLevelDiscount]));
    //     const discount = {
    //       type: 'percentage',
    //       value: this.roundOff(Math.abs((_order.total.orderDiscountItems[defaultOrderDiscountIndex].value * 100) / discountApplicableAmount)),
    //     };

    //     _order.total.orderDiscountItems[defaultOrderDiscountIndex] = {
    //       ..._order.total.orderDiscountItems[defaultOrderDiscountIndex],
    //       ...discount,
    //     };
    //   }
    // }

    if (!difference) return JSON.parse(JSON.stringify(_order));

    return null;
  }

  roRoundUpCalculation(order: any, tax: any, expectedValue: number, options: any = {}) {
    let _order = JSON.parse(JSON.stringify(order));
    let shopFee = 0;
    let shopSupplyFee: any = {};
    let shopSupplyIndex = -1;
    let actualShopSupplies = {};
    let actualShopFees = 0;

    if (_order.isRoLevelFeeEnabled) {
      _order.total.preTaxFee = _order.total.preTaxFee || [];
      shopSupplyIndex = findIndex(_order.total.preTaxFee, { isShopSupply: true });

      if (shopSupplyIndex === -1) {
        const defaultShopSupplies = find((options && options.orderFees) || [], { isShopSupply: true });

        if (defaultShopSupplies) {
          _order.total.preTaxFee.push({ ...defaultShopSupplies });
          shopSupplyIndex = findIndex(_order.total.preTaxFee, { isShopSupply: true });
        }

        actualShopSupplies = { ...defaultShopSupplies, value: 0 };
        actualShopFees = 0;
        shopFee = (shopSupplyFee && shopSupplyFee.feeCalcValue) || 0;
      } else {
        actualShopSupplies = { ..._order.total.preTaxFee[shopSupplyIndex] };
        shopFee = _order.total.preTaxFee[shopSupplyIndex].feeCalcValue || 0;
        actualShopFees = shopFee;
      }

      if (shopSupplyIndex !== -1) {
        shopSupplyFee = { ..._order.total.preTaxFee[shopSupplyIndex] };
        shopFee = (shopSupplyFee && shopSupplyFee.feeCalcValue) || 0;
      } else {
        return null;
      }

      shopSupplyFee = find(_order.total.preTaxFee, { isShopSupply: true });
    } else {
      actualShopSupplies = { ...(_order.total.shopSupplyFee || { value: 0, type: 'value' }) };
      shopFee = _order.total.shopFee;
    }

    let difference = this.roundOff(expectedValue - _order.total.grand);
    let updateShopFee = shopFee + difference;
    let iteration = 0;
    let actualOrderValue = _order.total.grand;

    console.log({ ...actualShopSupplies });
    while (difference && iteration < 20) {
      if (shopSupplyFee) {
        if (_order.isRoLevelFeeEnabled) {
          const index = findIndex(_order.total.preTaxFee, { isShopSupply: true });

          if (index !== -1) {
            _order.total.preTaxFee[index].type = 'value';
            _order.total.preTaxFee[index].value = updateShopFee;
            _order.total.preTaxFee[index].feeCalcValue = updateShopFee;
          }
        } else {
          _order.total.shopSupplyFee.type = 'value';
          _order.total.shopSupplyFee.value = updateShopFee;
        }
      }

      _order = this.estimateCalQuickbookCompatible(_order, tax, _order.total.discount);

      difference = difference = this.roundOff(expectedValue - _order.total.grand);

      if (_order.isRoLevelFeeEnabled) {
        const fee = find(_order.total.preTaxFee, { isShopSupply: true });

        if (fee) {
          updateShopFee = this.roundOff(fee.feeCalcValue + difference);
        }
      } else {
        updateShopFee = this.roundOff(_order.total.shopFee + difference);
      }

      iteration++;
    }

    if (_order) {
      _order.adjustments = {
        isRoundDown: false,
        adjustedValue: expectedValue,
        actualDiscount: {},
        actualShopSupplies,
        isApplied: true,
        actualOrderValue,
        actualShopFees,
      };
    }
    if (!difference) return JSON.parse(JSON.stringify(_order));

    return null;
  }

  estimateTotalWithAllServices(order: any) {
    let estimate = JSON.parse(JSON.stringify(order));
    let tax = estimate.total.tax;
    // let hazmatFee = estimate.total.hazmatFee || 0;
    let taxValue = 0;
    let roundOffTax = estimate.roundOffTax;
    let discount = estimate.total.discount;

    if (tax && tax.value) {
      taxValue = tax.value;
    } else if (tax && tax.salesTaxRate) {
      taxValue = tax.salesTaxRate;
    }

    // Tax Value object for recoard in estimate
    estimate.taxValues = {
      taxRate: taxValue,
      preDiscountTax: tax && tax.preDiscountTax,
      part: tax.parts,
      parts: tax.parts,
      labor: tax.labor,
      tire: tax.tire,
      other: tax.other,
      hazmatFee: tax.hazmatFee,
      tireStorage: tax.tireStorage,
      taxRates: (tax.taxRates || []).map((rate: any) => {
        rate.taxAmount = 0;
        return rate;
      }),
    };

    // Estimate total default object
    estimate.total = {
      parts: 0,
      labor: 0,
      tire: 0,
      hazmatFee: 0,
      tireStorage: 0,
      other: 0,
      sublet: 0,
      total: 0,
      netTotal: 0,
      itemTypeDiscount: {
        part: 0,
        labor: 0,
        tire: 0,
        hazmatFee: 0,
        tireStorage: 0,
        other: 0,
        sublet: 0,
      },
      afterDiscount: 0,
      afterTax: 0,
      grand: 0,
      itemDiscountedAmount: 0,
      itemLevelDiscount: 0,
      serviceLevelDiscount: 0,
      discountedAmount: 0,
      taxedAmount: 0,
      taxableAmount: 0,
      nonTaxableAmount: 0,
      totalFee: 0,
      discount: discount,
      shopFee: 0,
      preTaxFee: estimate.total.preTaxFee,
      orderDiscountItems: estimate.total.orderDiscountItems || [],
      shopSupplyFee: (estimate.total && estimate.total.shopSupplyFee) || {},
      shopFeePercent: 0,
      paid: estimate.total.paid,
      unpaid: estimate.total.unpaid,
      tax: {
        type: 'percentage',
        value: taxValue,
        id: tax && tax.tax && tax.tax.id,
        qboId: tax && tax.tax && tax.tax.qboId,
        name: tax && tax.tax && tax.tax.name ? tax.tax.name : '',
      },
    };

    estimate.totalCost = {
      part: 0,
      labor: 0,
      tire: 0,
      hazmatFee: 0,
      tireStorage: 0,
      other: 0,
      sublet: 0,
    };
    estimate.billableHours = 0;

    // Total Discount Amount for Taxable Items
    // let taxableItemDiscountAmount = 0;
    // Include services which are marked as Include In Invoice
    const included = estimate.services;
    const itemDistDiscount = {
      part: 0,
      labor: 0,
      tire: 0,
      tireStorage: 0,
      other: 0,
      sublet: 0,
      hazmatFee: 0,
    };

    included.forEach((service: any) => {
      // Service total default object
      service.serviceTotal.part = 0;
      service.serviceTotal.labor = 0;
      service.serviceTotal.tire = 0;
      service.serviceTotal.hazmatFee = 0;
      service.serviceTotal.tireStorage = 0;
      service.serviceTotal.other = 0;
      service.serviceTotal.sublet = 0;
      service.serviceTotal.itemTypeDiscount = {
        part: 0,
        labor: 0,
        tire: 0,
        hazmatFee: 0,
        tireStorage: 0,
        other: 0,
        sublet: 0,
      };
      service.billableHours = 0;
      service.serviceTotal.itemLevelDiscount = 0;
      service.serviceTotal.serviceLevelDiscount = 0;
      service.serviceTotal.serviceDiscountAmt = 0;
      service.serviceTotal.taxAmt = 0;
      service.serviceTotal.serviceTaxableAmt = 0;
      service.serviceTotal.serviceNonTaxableAmt = 0;
      service.totalCost = {
        part: 0,
        labor: 0,
        tire: 0,
        hazmatFee: 0,
        tireStorage: 0,
        other: 0,
      };
      service.serviceTotal.netTotal = 0;

      // Total for labor, part, other, tire, tire storages, fees, bundles
      service.items.forEach((t: any) => {
        if (t.type && t.type !== 'other' && !t.itemId) return;

        if (t.type === 'discount') return;

        if (t.type === 'bundle') {
          t.itemTotal = {
            partPrice: 0,
            laborPrice: 0,
            tirePrice: 0,

            partCost: 0,
            laborCost: 0,
            tireCost: 0,

            partCount: 0,
            laborCount: 0,
            tireCount: 0,
          };

          t.subItems.forEach((subItem: any) => {
            if (subItem.type === 'labor') {
              t.itemTotal[`${subItem.type}Cost`] += this.roundOff(subItem.cost);
              service.totalCost[subItem.type] += this.roundOff(subItem.cost);
              estimate.totalCost[subItem.type] += this.roundOff(subItem.cost);
            } else {
              t.itemTotal[`${subItem.type}Cost`] += this.roundOff(subItem.cost * subItem.count);
              service.totalCost[subItem.type] += this.roundOff(subItem.cost * subItem.count);
              estimate.totalCost[subItem.type] += this.roundOff(subItem.cost * subItem.count);
            }

            t.itemTotal[`${subItem.type}Price`] += this.roundOff(subItem.flatRateAmount);
            t.itemTotal[`${subItem.type}Count`] += this.roundOff(subItem.count);

            subItem.price = this.roundOff(subItem.price);
            subItem.count = this.roundOff(subItem.count);
            subItem.subTotal = this.roundOff(subItem.flatRateAmount);
            subItem.netSubTotal = this.roundOff(subItem.flatRateAmount);
            subItem.netPrice = 0;
            subItem.taxAmt = 0;

            if (subItem.price >= 0) {
              // Calculating discount amount for the item
              const itemDiscount = this.calculateOrderItemDiscount(subItem);

              service.serviceTotal.itemTypeDiscount[subItem.type] = this.roundOff(service.serviceTotal.itemTypeDiscount[subItem.type] + itemDiscount);
              estimate.total.itemTypeDiscount[subItem.type] = this.roundOff(estimate.total.itemTypeDiscount[subItem.type] + itemDiscount);
              if (!subItem.isPassThroughTax) {
                // Calculating item net-amount
                subItem.netSubTotal = this.roundOff(subItem.subTotal - itemDiscount);
                // Calculating item net-price
                subItem.netPrice = subItem.count > 0 ? subItem.netSubTotal / subItem.count : 0;
                // Calculating service net-total
                service.serviceTotal.netTotal = this.roundOff(service.serviceTotal.netTotal + this.roundOff(subItem.netPrice * subItem.count));
              }
              service.serviceTotal.itemLevelDiscount = this.roundOff(service.serviceTotal.itemLevelDiscount + itemDiscount);
              service.serviceTotal.serviceDiscountAmt = this.roundOff(service.serviceTotal.serviceDiscountAmt + itemDiscount);
              service.serviceTotal[subItem.type] = this.roundOff(service.serviceTotal[subItem.type] + this.roundOff(subItem.subTotal));

              itemDistDiscount[subItem.type] = this.roundOff(itemDistDiscount[subItem.type] + itemDiscount);

              if (subItem.type === 'labor' && subItem.count >= 0) {
                service.billableHours += subItem.count;
                estimate.billableHours += subItem.count;
              }

              estimate.total.itemLevelDiscount = this.roundOff(estimate.total.itemLevelDiscount + itemDiscount);
            }
          });
        } else {
          if (t.type === 'labor') {
            service.totalCost[t.type] += this.roundOff(t.cost);
            estimate.totalCost[t.type] += this.roundOff(t.cost);
          } else {
            service.totalCost[t.type] += this.roundOff(t.cost * t.count);
            estimate.totalCost[t.type] += this.roundOff(t.cost * t.count);
          }

          t.price = this.roundOff(t.price);
          t.count = this.roundOff(t.count);
          if (!t.isPassThroughTax) {
            t.subTotal = this.roundOff(t.price * t.count);
          }
          t.netSubTotal = 0;
          t.netPrice = 0;
          t.taxAmt = 0;

          if (t.price >= 0) {
            // Calculating discount amount for the item
            const itemDiscount = this.calculateOrderItemDiscount(t);
            service.serviceTotal.itemTypeDiscount[t.type] = this.roundOff(service.serviceTotal.itemTypeDiscount[t.type] + itemDiscount);
            estimate.total.itemTypeDiscount[t.type] = this.roundOff(estimate.total.itemTypeDiscount[t.type] + itemDiscount);
            if (!t.isPassThroughTax) {
              // Calculating item net-amount
              t.netSubTotal = this.roundOff(t.subTotal - itemDiscount);
              // Calculating item net-price
              t.netPrice = t.count > 0 ? t.netSubTotal / t.count : 0;
              // Calculating service net-total
              service.serviceTotal.netTotal = this.roundOff(service.serviceTotal.netTotal + this.roundOff(t.netPrice * t.count));
            }
            service.serviceTotal.itemLevelDiscount = this.roundOff(service.serviceTotal.itemLevelDiscount + itemDiscount);
            service.serviceTotal.serviceDiscountAmt = this.roundOff(service.serviceTotal.serviceDiscountAmt + itemDiscount);
            service.serviceTotal[t.type] = this.roundOff(service.serviceTotal[t.type] + this.roundOff(t.subTotal));

            itemDistDiscount[t.type] = this.roundOff(itemDistDiscount[t.type] + itemDiscount);

            if (t.type === 'labor' && t.count >= 0) {
              service.billableHours += t.count;
              estimate.billableHours += t.count;
            }

            estimate.total.itemLevelDiscount = this.roundOff(estimate.total.itemLevelDiscount + itemDiscount);
          }
        }
      });

      // Only for service discounts
      service.items.forEach((item: any) => {
        if (item.type === 'discount') {
          if (item.discountType === 'percentage') {
            let discount = this.roundOff((item.discountValue / 100) * service.serviceTotal.netTotal);

            if (discount > item.discountCap) {
              discount = item.discountCap;
            }

            item.price = discount;
            item.discountPercentage = item.discountValue;
          } else {
            item.discountPercentage = service.serviceTotal.netTotal ? this.roundOff(this.roundOff(item.price * 100) / service.serviceTotal.netTotal) : 0;
          }

          item.price = this.roundOff(item.price);
          item.count = this.roundOff(item.count);
          item.subTotal = this.roundOff(item.price * item.count);
          item.netPrice = item.price;
          item.netSubTotal = item.subTotal;
          item.taxAmt = 0;

          service.serviceTotal.serviceLevelDiscount = this.roundOff(service.serviceTotal.serviceLevelDiscount + item.price);

          if (roundOffTax) {
            service.serviceTotal.serviceDiscountAmt = this.roundOff(service.serviceTotal.serviceDiscountAmt + item.price);
          }

          estimate.total.serviceLevelDiscount = this.roundOff(estimate.total.serviceLevelDiscount + item.price);
        }
      });

      // Calculation for discounts

      // Subtotal for all services labor, parts, tire, other without tax and discount
      service.serviceTotal.total = this.roundOff(
        service.serviceTotal.part + service.serviceTotal.labor + service.serviceTotal.tire + service.serviceTotal.hazmatFee + service.serviceTotal.tireStorage + service.serviceTotal.other,
      );

      // Individual Total for all services labor, parts, tire, other
      estimate.total.labor = this.roundOff(estimate.total.labor + service.serviceTotal.labor);
      estimate.total.parts = this.roundOff(estimate.total.parts + service.serviceTotal.part);
      estimate.total.tire = this.roundOff(estimate.total.tire + service.serviceTotal.tire);
      estimate.total.hazmatFee = this.roundOff(estimate.total.hazmatFee + service.serviceTotal.hazmatFee);
      estimate.total.tireStorage = this.roundOff(estimate.total.tireStorage + service.serviceTotal.tireStorage);
      estimate.total.other = this.roundOff(estimate.total.other + service.serviceTotal.other);

      // Total service-discounts
      estimate.total.itemDiscountedAmount = this.roundOff(estimate.total.itemDiscountedAmount + this.roundOff(service.serviceTotal.serviceDiscountAmt));
    });

    estimate.total.total = this.roundOff(estimate.total.labor + estimate.total.parts + estimate.total.other + estimate.total.tire + estimate.total.hazmatFee + estimate.total.tireStorage);
    estimate.total.afterDiscount = this.roundOff(estimate.total.total - estimate.total.itemDiscountedAmount);

    if (estimate.isRoLevelFeeEnabled) {
      if (estimate.total.preTaxFee && estimate.total.preTaxFee.length) {
        estimate.total.preTaxFee.forEach((fee: any, index: number) => {
          if (fee.type === 'percentage') {
            if (fee.isApplicableOnAll) {
              estimate.total.preTaxFee[index].feeCalcValue = this.roundOff(estimate.total.afterDiscount * ((fee.value || 0) / 100));
            } else {
              if (fee.appliesOn === 'part') {
                estimate.total.preTaxFee[index].feeCalcValue = this.roundOff((estimate.total.parts - itemDistDiscount.part) * ((fee.value || 0) / 100));
              } else {
                estimate.total.preTaxFee[index].feeCalcValue = this.roundOff((estimate.total[fee.appliesOn] - itemDistDiscount[fee.appliesOn]) * ((fee.value || 0) / 100));
              }
            }

            if (estimate.total.afterDiscount && fee.cap && estimate.total.preTaxFee[index].feeCalcValue > fee.cap) {
              estimate.total.preTaxFee[index].feeCalcValue = fee.cap;
              estimate.total.preTaxFee[index].feeCalcPercent = ((estimate.total.preTaxFee[index].feeCalcValue || 0) * 100) / estimate.total.afterDiscount;
            } else {
              estimate.total.preTaxFee[index].feeCalcPercent = fee.value || 0;
            }
          } else {
            estimate.total.preTaxFee[index].feeCalcValue = this.roundOff(fee.value || 0);

            if (estimate.total.afterDiscount) {
              estimate.total.preTaxFee[index].feeCalcPercent = ((estimate.total.preTaxFee[index].feeCalcValue || 0) * 100) / estimate.total.afterDiscount;
            }
          }
        });

        estimate.total.totalFee = estimate.total.preTaxFee.map(({ feeCalcValue = 0 }) => feeCalcValue).reduce((total: number, curr: any) => total + curr);
      }

      estimate.total.afterDiscount = this.roundOff(estimate.total.afterDiscount + estimate.total.totalFee);
    } else {
      if (estimate.isShopFeeEnabled && estimate.total.shopSupplyFee && estimate.total.shopSupplyFee.value) {
        if (estimate.total.shopSupplyFee.type === 'percentage') {
          estimate.total.shopFee = this.roundOff(estimate.total.afterDiscount * ((estimate.total.shopSupplyFee.value || 0) / 100));

          if (estimate.total.afterDiscount && estimate.total.shopSupplyFee.cap && estimate.total.shopFee > estimate.total.shopSupplyFee.cap) {
            estimate.total.shopFee = estimate.total.shopSupplyFee.cap;
            estimate.total.shopFeePercent = ((estimate.total.shopFee || 0) * 100) / estimate.total.afterDiscount;
          } else {
            estimate.total.shopFeePercent = estimate.total.shopSupplyFee.value || 0;
          }
        } else {
          estimate.total.shopFee = this.roundOff(estimate.total.shopSupplyFee.value || 0);

          if (estimate.total.afterDiscount) {
            estimate.total.shopFeePercent = ((estimate.total.shopFee || 0) * 100) / estimate.total.afterDiscount;
          }
        }
      }

      estimate.total.shopFeePercent = this.roundOff(estimate.total.shopFeePercent);
      estimate.total.afterDiscount = this.roundOff(estimate.total.afterDiscount + estimate.total.shopFee);
    }

    // Calculating discount after subtracting the service discount
    if (estimate.isDiscountItemEnabled) {
      let totalDiscount = 0;

      if (estimate.total.orderDiscountItems && estimate.total.orderDiscountItems.length) {
        estimate.total.orderDiscountItems.forEach((discount: any) => {
          let discountValue = 0;

          if (discount.type === 'percentage') {
            discountValue = this.roundOff(estimate.total.afterDiscount * (discount.value / 100));
            discount.discountCalcPercent = discount.value;

            if (discount.cap && discountValue > discount.cap) {
              discountValue = discount.cap;
            }
          } else {
            discountValue = discount.value;
            discount.discountCalcPercent = this.roundOff((discountValue * 100) / estimate.total.afterDiscount);
          }

          discount.discountCalcValue = discountValue;
          totalDiscount = this.roundOff(totalDiscount + discount.discountCalcValue);
        });
      }

      estimate.total.discountedAmount = totalDiscount;
    } else {
      if (discount.type === 'percentage') {
        estimate.total.discountedAmount = this.roundOff(estimate.total.afterDiscount * (discount.value / 100));
      } else if (discount.type === 'value') {
        // Only apply discount if amount is greater then the discount
        if (estimate.total.afterDiscount >= discount.value || estimate.total.shopFee + estimate.total.totalFee >= discount.value) {
          estimate.total.discountedAmount = discount.value;
        }
      }
    }

    if (estimate.isRoLevelFeeEnabled) {
      if (estimate.total.preTaxFee && estimate.total.preTaxFee.length) {
        estimate.total.preTaxFee.forEach((fee: any, index: number) => {
          // Tax calculation for shop supplies
          const feePropDiscount = this.calculateOrderItemPropDiscount({
            roundOffTax,
            total: estimate.total.afterDiscount,
            netSubtotal: fee.feeCalcValue,
            discountedAmount: estimate.total.discountedAmount,
          });

          // Calculating shop supplies taxes
          let feeTaxAmount = 0;

          if (fee.taxable) {
            if (estimate.taxValues.taxRates && estimate.taxValues.taxRates.length) {
              const taxData = this.calculateOrderItemTaxes({
                total: fee.feeCalcValue - feePropDiscount,
                taxRates: estimate.taxValues.taxRates,
                roundOffTax,
              });
              feeTaxAmount = taxData.taxAmt;
              estimate.taxValues.taxRates = this.calculateTaxRatesAmount({
                taxRates: estimate.taxValues.taxRates,
                taxAmounts: taxData,
                roundOffTax,
              });
            } else {
              feeTaxAmount = this.calculateOrderItemTax({
                total: fee.feeCalcValue - feePropDiscount,
                taxValue,
                roundOffTax,
              });
            }
          }

          if (fee.taxable) {
            if (roundOffTax) {
              estimate.total.taxedAmount = this.roundOff(estimate.total.taxedAmount + feeTaxAmount);
              estimate.total.taxableAmount = this.roundOff(estimate.total.taxableAmount + estimate.total.preTaxFee[index].feeCalcValue - feePropDiscount);
            } else {
              estimate.total.taxedAmount += feeTaxAmount;
              estimate.total.taxableAmount += estimate.total.preTaxFee[index].feeCalcValue - feePropDiscount;
            }
          } else {
            if (roundOffTax) {
              estimate.total.nonTaxableAmount += this.roundOff(estimate.total.preTaxFee[index].feeCalcValue - feePropDiscount);
            } else {
              estimate.total.nonTaxableAmount += estimate.total.preTaxFee[index].feeCalcValue - feePropDiscount;
            }
          }
        });
      }
    } else {
      // Tax calculation for shop supplies
      const shopFeePropDiscount = this.calculateOrderItemPropDiscount({
        roundOffTax,
        total: estimate.total.afterDiscount,
        netSubtotal: estimate.total.shopFee,
        discountedAmount: estimate.total.discountedAmount,
      });

      // Calculating shop supplies taxes
      let shopFeeTaxAmount = 0;

      if (estimate.taxValues.taxRates && estimate.taxValues.taxRates.length) {
        const taxData = this.calculateOrderItemTaxes({
          total: estimate.total.shopFee - shopFeePropDiscount,
          taxRates: estimate.taxValues.taxRates,
          roundOffTax,
        });
        shopFeeTaxAmount = taxData.taxAmt;
        estimate.taxValues.taxRates = this.calculateTaxRatesAmount({
          taxRates: estimate.taxValues.taxRates,
          taxAmounts: taxData,
          roundOffTax,
        });
      } else {
        shopFeeTaxAmount = this.calculateOrderItemTax({
          total: estimate.total.shopFee - shopFeePropDiscount,
          taxValue,
          roundOffTax,
        });
      }

      if (roundOffTax) {
        estimate.total.taxedAmount = this.roundOff(shopFeeTaxAmount);
        estimate.total.taxableAmount = this.roundOff(estimate.total.shopFee - shopFeePropDiscount);
      } else {
        estimate.total.taxedAmount = shopFeeTaxAmount;
        estimate.total.taxableAmount = estimate.total.shopFee - shopFeePropDiscount;
      }
    }

    // Service Tax calculation
    included.forEach((e: any) => {
      // Total for labor, part, other
      e.items.forEach((t: any) => {
        if (t.type && t.type !== 'other' && !t.itemId) return;
        if (t.type === 'discount') return;

        t.taxAmt = 0;
        if (t.type === 'bundle') {
          t.subItems.forEach((subItem: any) => {
            if (subItem.price >= 0) {
              const propDiscount = this.calculateOrderItemPropDiscount({
                roundOffTax,
                total: estimate.total.afterDiscount,
                netSubtotal: subItem.netSubTotal,
                discountedAmount: estimate.total.discountedAmount,
              });

              if (estimate.taxValues[subItem.type] && subItem.isTaxable) {
                if (roundOffTax) {
                  e.serviceTotal.serviceTaxableAmt = this.roundOff(e.serviceTotal.serviceTaxableAmt + (subItem.netSubTotal - propDiscount));

                  // Calculating taxes
                  if (estimate.taxValues.taxRates && estimate.taxValues.taxRates.length) {
                    const taxData = this.calculateOrderItemTaxes({
                      total: subItem.netSubTotal - propDiscount,
                      taxRates: estimate.taxValues.taxRates,
                      roundOffTax,
                      itemType: subItem.type,
                    });
                    subItem.taxAmt = this.roundOff(taxData.taxAmt + subItem.taxAmt);
                    estimate.taxValues.taxRates = this.calculateTaxRatesAmount({
                      taxRates: estimate.taxValues.taxRates,
                      taxAmounts: taxData,
                      roundOffTax,
                    });
                  } else {
                    subItem.taxAmt = this.roundOff(
                      subItem.taxAmt +
                      this.calculateOrderItemTax({
                        total: subItem.netSubTotal - propDiscount,
                        taxValue,
                        roundOffTax,
                      }),
                    );
                  }

                  e.serviceTotal.taxAmt = this.roundOff(e.serviceTotal.taxAmt + subItem.taxAmt);
                } else {
                  e.serviceTotal.serviceTaxableAmt = e.serviceTotal.serviceTaxableAmt + (subItem.netSubTotal - propDiscount);

                  // Calculating tax
                  if (estimate.taxValues.taxRates && estimate.taxValues.taxRates.length) {
                    const taxData = this.calculateOrderItemTaxes({
                      total: subItem.netSubTotal - propDiscount,
                      taxRates: estimate.taxValues.taxRates,
                      roundOffTax,
                      itemType: subItem.type,
                    });
                    subItem.taxAmt = taxData.taxAmt + subItem.taxAmt;
                    estimate.taxValues.taxRates = this.calculateTaxRatesAmount({
                      taxRates: estimate.taxValues.taxRates,
                      taxAmounts: taxData,
                      roundOffTax,
                    });
                  } else {
                    subItem.taxAmt =
                      subItem.taxAmt +
                      this.calculateOrderItemTax({
                        total: subItem.netSubTotal - propDiscount,
                        taxValue,
                        roundOffTax,
                      });
                  }

                  e.serviceTotal.taxAmt = e.serviceTotal.taxAmt + subItem.taxAmt;
                }
              } else {
                e.serviceTotal.serviceNonTaxableAmt = this.roundOff(e.serviceTotal.serviceNonTaxableAmt + this.roundOff(subItem.netSubTotal - propDiscount));
              }
            }
          });
        } else {
          if (t.price >= 0) {
            const propDiscount = this.calculateOrderItemPropDiscount({
              roundOffTax,
              total: estimate.total.afterDiscount,
              netSubtotal: t.netSubTotal,
              discountedAmount: estimate.total.discountedAmount,
            });

            t.propDiscount = propDiscount;

            if (estimate.taxValues[t.type] && t.isTaxable) {
              if (roundOffTax) {
                e.serviceTotal.serviceTaxableAmt = this.roundOff(e.serviceTotal.serviceTaxableAmt + (t.netSubTotal - propDiscount));

                // Calculating taxes
                if (estimate.taxValues.taxRates && estimate.taxValues.taxRates.length) {
                  const taxData = this.calculateOrderItemTaxes({
                    total: t.netSubTotal - propDiscount,
                    taxRates: estimate.taxValues.taxRates,
                    roundOffTax,
                    itemType: t.type,
                  });
                  t.taxAmt = this.roundOff(taxData.taxAmt + t.taxAmt);
                  estimate.taxValues.taxRates = this.calculateTaxRatesAmount({
                    taxRates: estimate.taxValues.taxRates,
                    taxAmounts: taxData,
                    roundOffTax,
                  });
                } else {
                  t.taxAmt = this.roundOff(
                    t.taxAmt +
                    this.calculateOrderItemTax({
                      total: t.netSubTotal - propDiscount,
                      taxValue,
                      roundOffTax,
                    }),
                  );
                }

                e.serviceTotal.taxAmt = this.roundOff(e.serviceTotal.taxAmt + t.taxAmt);
              } else {
                e.serviceTotal.serviceTaxableAmt = e.serviceTotal.serviceTaxableAmt + (t.netSubTotal - propDiscount);

                // Calculating tax
                if (estimate.taxValues.taxRates && estimate.taxValues.taxRates.length) {
                  const taxData = this.calculateOrderItemTaxes({
                    total: t.netSubTotal - propDiscount,
                    taxRates: estimate.taxValues.taxRates,
                    roundOffTax,
                    itemType: t.type,
                  });
                  t.taxAmt = taxData.taxAmt + t.taxAmt;
                  estimate.taxValues.taxRates = this.calculateTaxRatesAmount({
                    taxRates: estimate.taxValues.taxRates,
                    taxAmounts: taxData,
                    roundOffTax,
                  });
                } else {
                  t.taxAmt =
                    t.taxAmt +
                    this.calculateOrderItemTax({
                      total: t.netSubTotal - propDiscount,
                      taxValue,
                      roundOffTax,
                    });
                }

                e.serviceTotal.taxAmt = e.serviceTotal.taxAmt + t.taxAmt;
              }
            } else {
              e.serviceTotal.serviceNonTaxableAmt = this.roundOff(e.serviceTotal.serviceNonTaxableAmt + this.roundOff(t.netSubTotal - propDiscount));
            }
          }
        }
      });

      if (roundOffTax) {
        estimate.total.taxedAmount = this.roundOff(estimate.total.taxedAmount + e.serviceTotal.taxAmt);
      } else {
        estimate.total.taxedAmount = estimate.total.taxedAmount + e.serviceTotal.taxAmt;
      }

      // Total taxable amount
      if (roundOffTax) {
        estimate.total.taxableAmount = this.roundOff(estimate.total.taxableAmount + e.serviceTotal.serviceTaxableAmt);
      } else {
        estimate.total.taxableAmount = estimate.total.taxableAmount + e.serviceTotal.serviceTaxableAmt;
      }

      estimate.total.nonTaxableAmount = this.roundOff(estimate.total.nonTaxableAmount + e.serviceTotal.serviceNonTaxableAmt);
    });

    estimate.total.nonTaxableAmount = this.roundOff(estimate.total.nonTaxableAmount - estimate.total.serviceLevelDiscount);
    estimate.total.afterDiscount = this.roundOff(estimate.total.afterDiscount - estimate.total.discountedAmount);

    if (tax && !tax.preDiscountTax) {
      if (roundOffTax) {
        estimate.total.afterTax = this.roundOff(estimate.total.afterDiscount + estimate.total.taxedAmount);
      } else {
        estimate.total.afterTax = this.roundOff(estimate.total.afterDiscount + estimate.total.taxedAmount) - estimate.total.serviceLevelDiscount;
        estimate.total.afterDiscount -= estimate.total.serviceLevelDiscount;
      }

      estimate.total.grand = this.roundOff(estimate.total.afterTax);
    }

    return estimate.total.grand;
  }

  estimateCalQuickbookCompatible(estimate, tax, discount) {
    // Tax rate value
    let taxValue = 0;
    let roundOffTax = estimate.roundOffTax; // If true Canada else USA
    if (tax && tax.tax) {
      if (tax.tax.value) {
        taxValue = tax.tax.value;
      } else if (tax.tax.salesTaxRate) {
        taxValue = tax.tax.salesTaxRate;
      }
    }

    // Tax Value object for recoard in estimate
    estimate.taxValues = {
      taxRate: taxValue,
      preDiscountTax: tax && tax.preDiscountTax,
      part: tax.parts,
      parts: tax.parts,
      labor: tax.labor,
      tire: tax.tire,
      other: tax.other,
      sublet: true,
      discount: true,
      hazmatFee: tax.hazmatFee,
      tireStorage: tax.tireStorage,
      taxRates: (tax.taxRates || []).map((rate) => {
        rate.taxAmount = 0;
        return rate;
      }),
    };

    // Estimate total default object
    estimate.total = {
      parts: 0,
      labor: 0,
      tire: 0,
      hazmatFee: 0,
      passThroughTax: 0,
      tireStorage: 0,
      other: 0,
      sublet: 0,
      total: 0,
      netTotal: 0,
      itemTypeDiscount: {
        part: 0,
        labor: 0,
        tire: 0,
        hazmatFee: 0,
        tireStorage: 0,
        other: 0,
        sublet: 0,
      },
      afterDiscount: 0,
      discountApplicableAmount: 0,
      afterTax: 0,
      grand: 0,
      itemDiscountedAmount: 0,
      itemLevelDiscount: 0,
      serviceLevelDiscount: 0,
      discountedAmount: 0,
      taxedAmount: 0,
      taxableAmount: 0,
      nonTaxableAmount: 0,
      totalFee: 0,
      discount: discount,
      shopFee: 0,
      preTaxFee: estimate.total.preTaxFee,
      orderDiscountItems: estimate.total.orderDiscountItems || [],
      shopSupplyFee: (estimate.total && estimate.total.shopSupplyFee) || {},
      shopFeePercent: 0,
      paid: this.roundOff(estimate.total.paid),
      unpaid: this.roundOff(estimate.total.unpaid),
      tax: {
        type: 'percentage',
        value: taxValue,
        id: tax && tax.tax && tax.tax.id,
        qboId: tax && tax.tax && tax.tax.qboId,
        name: tax && tax.tax && tax.tax.name ? tax.tax.name : '',
      },
    };
    estimate.totalCost = {
      part: 0,
      labor: 0,
      tire: 0,
      hazmatFee: 0,
      passThroughTax: 0,
      tireStorage: 0,
      other: 0,
      sublet: 0,
    };
    estimate.billableHours = 0;
    const isPassThroughTax = estimate.services.some((service) => service.items.some((item) => item.type === 'hazmatFee' && item.isPassThroughTax));

    // Total Discount Amount for Taxable Items
    // let taxableItemDiscountAmount = 0;
    // Include services which are marked as Include In Invoice
    const included = estimate.services.filter(({ includeInInvoice }) => includeInInvoice);
    const notIncluded = estimate.services.filter(({ includeInInvoice }) => !includeInInvoice);
    const itemDistDiscount = {
      part: 0,
      labor: 0,
      tire: 0,
      tireStorage: 0,
      other: 0,
      sublet: 0,
      hazmatFee: 0,
    };
    let serviceTaxableDiscount = 0;
    let serviceNonTaxableDiscount = 0;

    notIncluded.forEach((service) => {
      // Service total default object
      service.serviceTotal.part = 0;
      service.serviceTotal.labor = 0;
      service.serviceTotal.tire = 0;
      service.serviceTotal.hazmatFee = 0;
      service.serviceTotal.passThroughTax = 0;
      service.serviceTotal.tireStorage = 0;
      service.serviceTotal.other = 0;
      service.serviceTotal.sublet = 0;
      service.billableHours = 0;
      service.serviceTotal.serviceDiscountAmt = 0;
      service.serviceTotal.itemLevelDiscount = 0;
      service.serviceTotal.serviceLevelDiscount = 0;
      service.serviceTotal.serviceTaxableAmt = 0;
      service.serviceTotal.serviceNonTaxableAmt = 0;
      service.totalCost = {
        part: 0,
        labor: 0,
        tire: 0,
        hazmatFee: 0,
        tireStorage: 0,
        other: 0,
        sublet: 0,
      };

      // Total for labor, part, other
      service.items.forEach((t) => {
        // if (t.type && t.type !== 'other' && !t.itemId) {
        if (t.type && !['other', 'sublet'].includes(t.type) && !t.itemId) {
          return;
        }

        if (t.type === 'bundle') {
          t.subItems.forEach((subItem) => {
            if (subItem.type === 'discount') {
              if (subItem.discountType === 'value') {
                service.serviceTotal.serviceLevelDiscount += subItem.price;
                service.serviceTotal.serviceDiscountAmt += subItem.price;
              }
            }

            subItem.price = this.roundOff(subItem.price);
            subItem.count = this.roundOff(subItem.count);
            subItem.subTotal = this.roundOff(subItem.flatRateAmount);
            subItem.netSubTotal = this.roundOff(subItem.flatRateAmount);
            if (subItem.price >= 0) {
              // Calculating discount amount for the item
              const itemDiscount = this.calculateOrderItemDiscount(t);

              if (estimate.taxValues[subItem.type] && subItem.isTaxable) {
                service.serviceTotal.serviceTaxableAmt += this.roundOff(subItem.subTotal - itemDiscount);
              } else {
                service.serviceTotal.serviceNonTaxableAmt += this.roundOff(subItem.subTotal - itemDiscount);
              }

              service.serviceTotal.itemLevelDiscount += itemDiscount;
              service.serviceTotal.serviceDiscountAmt += itemDiscount;
              if (subItem.isPassThroughTax) {
                service.serviceTotal['passThroughTax'] += this.roundOff(subItem.subTotal);
              } else {
                service.serviceTotal[subItem.type] += this.roundOff(subItem.subTotal);
              }

              if (subItem.type === 'labor' && subItem.count >= 0) {
                service.billableHours += subItem.count;
                estimate.billableHours += subItem.count;
              }
            }
          });
        } else {
          if (t.type === 'discount') {
            if (t.discountType === 'value') {
              service.serviceTotal.serviceLevelDiscount += t.price;
              service.serviceTotal.serviceDiscountAmt += t.price;
            }
          }

          t.price = this.roundOff(t.price);
          t.count = this.roundOff(t.count);
          t.subTotal = this.roundOff(t.price * t.count);
          t.netSubTotal = 0;

          if (t.price >= 0) {
            // Calculating discount amount for the item
            const itemDiscount = this.calculateOrderItemDiscount(t);

            if (estimate.taxValues[t.type] && t.isTaxable) {
              service.serviceTotal.serviceTaxableAmt += this.roundOff(t.subTotal - itemDiscount);
            } else {
              service.serviceTotal.serviceNonTaxableAmt += this.roundOff(t.subTotal - itemDiscount);
            }

            service.serviceTotal.itemLevelDiscount += itemDiscount;
            service.serviceTotal.serviceDiscountAmt += itemDiscount;
            if (t.isPassThroughTax) {
              service.serviceTotal['passThroughTax'] += this.roundOff(t.subTotal);
              t.netSubTotal = this.roundOff(t.subTotal);
            } else {
              service.serviceTotal[t.type] += this.roundOff(t.subTotal);
              t.netSubTotal = this.roundOff(t.subTotal - itemDiscount);
            }

            if (t.type === 'labor' && t.count >= 0) {
              service.billableHours += t.count;
              estimate.billableHours += t.count;
            }
          }
        }
      });

      // Subtotal for all services labor, parts, other  without tax and discount
      service.serviceTotal.total = this.roundOff(
        service.serviceTotal.part +
        service.serviceTotal.labor +
        service.serviceTotal.tire +
        service.serviceTotal.hazmatFee +
        service.serviceTotal.tireStorage +
        service.serviceTotal.other +
        service.serviceTotal?.sublet,
      );
    });

    included.forEach((service) => {
      // Service total default object
      service.serviceTotal.part = 0;
      service.serviceTotal.labor = 0;
      service.serviceTotal.tire = 0;
      service.serviceTotal.hazmatFee = 0;
      service.serviceTotal.passThroughTax = 0;
      service.serviceTotal.tireStorage = 0;
      service.serviceTotal.other = 0;
      service.serviceTotal.sublet = 0;
      service.serviceTotal.itemTypeDiscount = {
        part: 0,
        labor: 0,
        tire: 0,
        hazmatFee: 0,
        tireStorage: 0,
        other: 0,
        sublet: 0,
      };
      service.billableHours = 0;
      service.serviceTotal.itemLevelDiscount = 0;
      service.serviceTotal.serviceLevelDiscount = 0;
      service.serviceTotal.serviceDiscountAmt = 0;
      service.serviceTotal.taxAmt = 0;
      service.serviceTotal.serviceTaxableAmt = 0;
      service.serviceTotal.serviceNonTaxableAmt = 0;
      service.totalCost = {
        part: 0,
        labor: 0,
        tire: 0,
        hazmatFee: 0,
        passThroughTax: 0,
        tireStorage: 0,
        other: 0,
        sublet: 0,
      };
      service.serviceTotal.netTotal = 0;

      // Total for labor, part, other, tire, tire storages, fees, bundles
      service.items.forEach((t) => {
        // if (t.type && !['other', 'sublet'].includes(t.type) && !t.itemId) return;

        if (t.type === 'discount') return;

        if (t.type === 'bundle') {
          t.itemTotal = {
            partPrice: 0,
            laborPrice: 0,
            tirePrice: 0,

            partCost: 0,
            laborCost: 0,
            tireCost: 0,

            partCount: 0,
            laborCount: 0,
            tireCount: 0,
          };

          t.subItems?.forEach((subItem) => {
            if (subItem.type === 'labor' || (subItem.type === 'hazmatFee' && subItem.feeValueType === 'percent')) {
              const factor = 1;
              // const factor = t.lineItemType === 'refund' ? -1 : 1;

              if (subItem.isPassThroughTax) {
                t.itemTotal[`passThroughTaxCost`] += this.roundOff(subItem.cost * factor);
                service.totalCost['passThroughTax'] += this.roundOff(subItem.cost * factor);
                estimate.totalCost['passThroughTax'] += this.roundOff(subItem.cost * factor);
              } else {
                t.itemTotal[`${subItem.type}Cost`] += this.roundOff(subItem.cost * factor);
                service.totalCost[subItem.type] += this.roundOff(subItem.cost * factor);
                estimate.totalCost[subItem.type] += this.roundOff(subItem.cost * factor);
              }
            } else {
              if (subItem.isPassThroughTax) {
                t.itemTotal[`passThroughTaxCost`] += this.roundOff(subItem.cost * subItem.count);
                service.totalCost['passThroughTax'] += this.roundOff(subItem.cost * subItem.count);
                estimate.totalCost['passThroughTax'] += this.roundOff(subItem.cost * subItem.count);
              } else {
                t.itemTotal[`${subItem.type}Cost`] += this.roundOff(subItem.cost * subItem.count);
                service.totalCost[subItem.type] += this.roundOff(subItem.cost * subItem.count);
                estimate.totalCost[subItem.type] += this.roundOff(subItem.cost * subItem.count);
              }
            }

            t.itemTotal[`${subItem.type}Price`] += this.roundOff(subItem.flatRateAmount);
            t.itemTotal[`${subItem.type}Count`] += this.roundOff(subItem.count);

            subItem.price = this.roundOff(subItem.price);
            subItem.count = this.roundOff(subItem.count);

            // if (!subItem.isPassThroughTax) {
            subItem.subTotal = this.roundOff(subItem.flatRateAmount);
            subItem.netSubTotal = this.roundOff(subItem.flatRateAmount);
            // }

            subItem.netPrice = 0;
            subItem.taxAmt = 0;

            if (subItem.price >= 0) {
              // Calculating discount amount for the item
              const itemDiscount = this.calculateOrderItemDiscount(subItem);
              subItem.discountAmt = itemDiscount;
              service.serviceTotal.itemTypeDiscount[subItem.type] = this.roundOff(service.serviceTotal.itemTypeDiscount[subItem.type] + itemDiscount);
              estimate.total.itemTypeDiscount[subItem.type] = this.roundOff(estimate.total.itemTypeDiscount[subItem.type] + itemDiscount);
              if (!subItem.isPassThroughTax) {
                // Calculating item net-amount
                subItem.netSubTotal = this.roundOff(subItem.subTotal - itemDiscount);
                // Calculating item net-price
                subItem.netPrice = subItem.count > 0 ? subItem.netSubTotal / subItem.count : 0;
                // Calculating service net-total
                service.serviceTotal.netTotal = this.roundOff(service.serviceTotal.netTotal + this.roundOff(subItem.netPrice * subItem.count));
              }

              service.serviceTotal.itemLevelDiscount = this.roundOff(service.serviceTotal.itemLevelDiscount + itemDiscount);
              service.serviceTotal.serviceDiscountAmt = this.roundOff(service.serviceTotal.serviceDiscountAmt + itemDiscount);
              if (subItem.isPassThroughTax) {
                service.serviceTotal['passThroughTax'] = this.roundOff(service.serviceTotal['passThroughTax'] + this.roundOff(subItem.subTotal));
              } else {
                service.serviceTotal[subItem.type] = this.roundOff(service.serviceTotal[subItem.type] + this.roundOff(subItem.subTotal));
              }
              itemDistDiscount[subItem.type] = this.roundOff(itemDistDiscount[subItem.type] + itemDiscount);

              if (subItem.type === 'labor' && subItem.count >= 0) {
                service.billableHours += subItem.count;
                estimate.billableHours += subItem.count;
              }

              estimate.total.itemLevelDiscount = this.roundOff(estimate.total.itemLevelDiscount + itemDiscount);
            }
          });
        } else {
          if (t.type === 'labor' || (t.type === 'hazmatFee' && t.feeValueType === 'percent')) {
            // const factor = t.lineItemType === 'refund' ? -1 : 1;
            const factor = 1;

            if (t.isPassThroughTax) {
              service.totalCost['passThroughTax'] += this.roundOff(t.cost * factor);
              estimate.totalCost['passThroughTax'] += this.roundOff(t.cost * factor);
            } else {
              service.totalCost[t.type] += this.roundOff(t.cost * factor);
              estimate.totalCost[t.type] += this.roundOff(t.cost * factor);
            }
          } else {
            if (t.isPassThroughTax) {
              service.totalCost['passThroughTax'] += this.roundOff(t.cost * t.count);
              estimate.totalCost['passThroughTax'] += this.roundOff(t.cost * t.count);
            } else {
              service.totalCost[t.type] += this.roundOff(t.cost * t.count);
              estimate.totalCost[t.type] += this.roundOff(t.cost * t.count);
            }
          }

          //ALP-18784 Round off to 3 decimal places for accurate calculations, but display only 2 decimal places.
          // t.price = this.roundOff(t.price);
          //in case of tire storage flat fee do not round off the price as it is saved as divided by its quantity and re evaluate flat fee runtime rounding off causing the value to round down in this case
          if (this.dataService.isTireStorage2Enabled && t?.type === 'tireStorage' && t?.pricingType === 'flatFee') {
            t.price = t.price;
          } else {
            t.price = this.roundOff(t.price, 3);
          }
          t.count = this.roundOff(t.count);
          t.subTotal = this.roundOff(t.price * t.count);
          t.netSubTotal = 0;
          t.netPrice = 0;
          t.taxAmt = 0;

          if (t.price >= 0) {
            // Calculating discount amount for the item
            const itemDiscount = this.calculateOrderItemDiscount(t);
            t.discountAmt = itemDiscount;
            service.serviceTotal.itemTypeDiscount[t.type] = this.roundOff(service.serviceTotal.itemTypeDiscount[t.type] + itemDiscount);
            estimate.total.itemTypeDiscount[t.type] = this.roundOff(estimate.total.itemTypeDiscount[t.type] + itemDiscount);

            if (!t.isPassThroughTax) {
              // Calculating item net-amount
              t.netSubTotal = this.roundOff(t.subTotal - itemDiscount);
              // Calculating item net-price
              t.netPrice = t.count > 0 ? t.netSubTotal / t.count : 0;
              // Calculating service net-total
            } else {
              // Calculating item net-amount
              t.netSubTotal = this.roundOff(t.subTotal);
              // Calculating item net-price
              t.netPrice = t.count > 0 ? t.netSubTotal / t.count : 0;
              // Calculating service net-total
            }
            service.serviceTotal.netTotal = this.roundOff(service.serviceTotal.netTotal + this.roundOff(t.netPrice * t.count));
            service.serviceTotal.itemLevelDiscount = this.roundOff(service.serviceTotal.itemLevelDiscount + itemDiscount);
            service.serviceTotal.serviceDiscountAmt = this.roundOff(service.serviceTotal.serviceDiscountAmt + itemDiscount);

            if (t.isPassThroughTax) {
              service.serviceTotal['passThroughTax'] = this.roundOff(service.serviceTotal['passThroughTax'] + this.roundOff(t.subTotal));
            } else {
              service.serviceTotal[t.type] = this.roundOff(service.serviceTotal[t.type] + this.roundOff(t.subTotal));
            }

            itemDistDiscount[t.type] = this.roundOff(itemDistDiscount[t.type] + itemDiscount);

            if (t.type === 'labor' && t.count >= 0) {
              service.billableHours += t.count;
              estimate.billableHours += t.count;
            }

            estimate.total.itemLevelDiscount = this.roundOff(estimate.total.itemLevelDiscount + itemDiscount);
          }
        }
      });

      // Only for service discounts
      service.items.forEach((item) => {
        if (item.type === 'discount') {
          if (item.discountType === 'percentage') {
            let discount = this.roundOff((item.discountValue / 100) * service.serviceTotal.netTotal);

            if (discount > item.discountCap) {
              discount = item.discountCap;
            }

            item.price = discount;
            item.discountPercentage = item.discountValue;
          } else {
            item.discountPercentage = service.serviceTotal.netTotal ? this.roundOff(this.roundOff(item.price * 100) / service.serviceTotal.netTotal) : 0;
          }

          item.price = this.roundOff(item.price);
          item.count = this.roundOff(item.count);
          item.subTotal = this.roundOff(item.price * item.count);
          item.netPrice = item.price;
          item.netSubTotal = item.subTotal;
          item.taxAmt = 0;
          service.serviceTotal.serviceLevelDiscount = isPassThroughTax ? 0 : this.roundOff(service.serviceTotal.serviceLevelDiscount + item.price);

          if (roundOffTax) {
            service.serviceTotal.serviceDiscountAmt = isPassThroughTax ? 0 : this.roundOff(service.serviceTotal.serviceDiscountAmt + item.price);
          }
          estimate.total.serviceLevelDiscount = isPassThroughTax ? 0 : this.roundOff(estimate.total.serviceLevelDiscount + item.price);


          // QuickBooks don't distrub discount on taxable -ve item, but distrub on non-taxable -ve item
          if (item.isTaxable) {
            if (roundOffTax) {
              serviceTaxableDiscount = this.roundOff(serviceTaxableDiscount + item.price);
            } else {
              serviceTaxableDiscount += item.price;
            }
          } else {
            if (roundOffTax) {
              serviceNonTaxableDiscount = this.roundOff(serviceNonTaxableDiscount + item.price);
            } else {
              serviceNonTaxableDiscount += item.price;
            }
          }
        }
      });

      // Calculation for discounts

      // Subtotal for all services labor, parts, tire, other without tax and discount
      service.serviceTotal.total = this.roundOff(
        service.serviceTotal.part +
        service.serviceTotal.labor +
        service.serviceTotal.tire +
        service.serviceTotal.hazmatFee +
        service.serviceTotal.tireStorage +
        service.serviceTotal.other +
        (service.serviceTotal?.sublet || 0),
      );

      // Individual Total for all services labor, parts, tire, other
      estimate.total.labor = this.roundOff(estimate.total.labor + service.serviceTotal.labor);
      estimate.total.parts = this.roundOff(estimate.total.parts + service.serviceTotal.part);
      estimate.total.tire = this.roundOff(estimate.total.tire + service.serviceTotal.tire);
      estimate.total.hazmatFee = this.roundOff(estimate.total.hazmatFee + service.serviceTotal.hazmatFee);
      estimate.total.passThroughTax = this.roundOff(estimate.total.passThroughTax + service.serviceTotal.passThroughTax);
      // estimate.total.passThroughTax = estimate.invoiceType
      //   ? -1 * this.roundOff(estimate.total.passThroughTax + service.serviceTotal.passThroughTax)
      //   : this.roundOff(estimate.total.passThroughTax + service.serviceTotal.passThroughTax);
      estimate.total.tireStorage = this.roundOff(estimate.total.tireStorage + service.serviceTotal.tireStorage);
      estimate.total.other = this.roundOff(estimate.total.other + service.serviceTotal.other);
      estimate.total.sublet = this.roundOff(estimate.total.sublet + service.serviceTotal.sublet);

      // Total service-discounts
      estimate.total.itemDiscountedAmount = this.roundOff(estimate.total.itemDiscountedAmount + this.roundOff(service.serviceTotal.serviceDiscountAmt));
    });

    const total = estimate.total;

    estimate.total.total = this.roundOff(total.labor + total.parts + total.other + total.tire + total.hazmatFee + total.tireStorage + total?.sublet);
    estimate.total.afterDiscount = this.roundOff(total.total - total.itemDiscountedAmount);
    estimate.total.discountApplicableAmount = this.roundOff(estimate.total.afterDiscount - serviceTaxableDiscount);

    if (estimate.isRoLevelFeeEnabled) {
      if (estimate.total.preTaxFee && estimate.total.preTaxFee.length) {
        estimate.total.preTaxFee.forEach((fee, index) => {
          if (fee.type === 'percentage') {
            if (fee.isApplicableOnAll) {
              estimate.total.preTaxFee[index].feeCalcValue = isPassThroughTax ? 0 : this.roundOff(estimate.total.afterDiscount * ((fee.value || 0) / 100));
            } else {
              if (fee.appliesOn === 'part') {
                estimate.total.preTaxFee[index].feeCalcValue = isPassThroughTax ? 0 : this.roundOff((estimate.total.parts - itemDistDiscount.part) * ((fee.value || 0) / 100));
              } else {
                estimate.total.preTaxFee[index].feeCalcValue = isPassThroughTax ? 0 : this.roundOff((estimate.total[fee.appliesOn] - itemDistDiscount[fee.appliesOn]) * ((fee.value || 0) / 100));
              }
            }

            if (estimate.total.afterDiscount && fee.cap && Math.abs(estimate.total.preTaxFee[index].feeCalcValue) > Math.abs(fee.cap)) {
              estimate.total.preTaxFee[index].feeCalcValue = isPassThroughTax ? 0 : fee.cap * (estimate.total.afterDiscount < 0 ? -1 : 1);
              estimate.total.preTaxFee[index].feeCalcPercent = ((estimate.total.preTaxFee[index].feeCalcValue || 0) * 100) / estimate.total.afterDiscount;
            } else {
              estimate.total.preTaxFee[index].feeCalcPercent = fee.value || 0;
            }

            estimate.total.discountApplicableAmount += estimate.total.preTaxFee[index].feeCalcValue;
          } else {
            estimate.total.preTaxFee[index].feeCalcValue = isPassThroughTax ? 0 : this.roundOff(fee.value || 0);

            if (estimate.total.afterDiscount) {
              estimate.total.preTaxFee[index].feeCalcPercent = ((estimate.total.preTaxFee[index].feeCalcValue || 0) * 100) / estimate.total.afterDiscount;
            }

            estimate.total.discountApplicableAmount += estimate.total.preTaxFee[index].feeCalcValue;
          }
        });

        estimate.total.totalFee = estimate.total.preTaxFee.map(({ feeCalcValue = 0 }) => feeCalcValue).reduce((total, curr) => total + curr);
      }

      estimate.total.afterDiscount = this.roundOff(estimate.total.afterDiscount + estimate.total.totalFee);
    } else {
      if (estimate.isShopFeeEnabled && estimate.total.shopSupplyFee && estimate.total.shopSupplyFee.value) {
        if (estimate.total.shopSupplyFee.type === 'percentage') {
          estimate.total.shopFee = this.roundOff(estimate.total.afterDiscount * ((estimate.total.shopSupplyFee.value || 0) / 100));

          if (estimate.total.afterDiscount && estimate.total.shopSupplyFee.cap && estimate.total.shopFee > estimate.total.shopSupplyFee.cap) {
            estimate.total.shopFee = estimate.total.shopSupplyFee.cap;
            estimate.total.shopFeePercent = ((estimate.total.shopFee || 0) * 100) / estimate.total.afterDiscount;
          } else {
            estimate.total.shopFeePercent = estimate.total.shopSupplyFee.value || 0;
          }
        } else {
          estimate.total.shopFee = this.roundOff(estimate.total.shopSupplyFee.value || 0);

          if (estimate.total.afterDiscount) {
            estimate.total.shopFeePercent = ((estimate.total.shopFee || 0) * 100) / estimate.total.afterDiscount;
          }
        }

        estimate.total.discountApplicableAmount += estimate.total.shopFee;
      }

      estimate.total.shopFeePercent = this.roundOff(estimate.total.shopFeePercent);
      estimate.total.afterDiscount = this.roundOff(estimate.total.afterDiscount + estimate.total.shopFee);
    }

    // Calculating discount after subtracting the service discount
    if (estimate.isDiscountItemEnabled) {
      let totalDiscount = 0;

      if (estimate.total.orderDiscountItems && estimate.total.orderDiscountItems.length) {
        estimate.total.orderDiscountItems.forEach((discount) => {
          let discountValue = 0;
          discount.value = this.roundOff(discount.value);

          if (discount.type === 'percentage') {
            discountValue = this.roundOff(estimate.total.afterDiscount * (discount.value / 100));
            discount.discountCalcPercent = discount.value;

            if (discount.cap && discountValue > discount.cap) {
              discountValue = discount.cap;
            }
          } else {
            discountValue = discount.value;
            discount.discountCalcPercent = this.roundOff((discountValue * 100) / estimate.total.afterDiscount);
          }

          discount.discountCalcValue = discountValue;
          totalDiscount = this.roundOff(totalDiscount + discount.discountCalcValue);
        });
      }

      estimate.total.discountedAmount = totalDiscount;
    } else {
      if (discount.type === 'percentage') {
        estimate.total.discountedAmount = this.roundOff(estimate.total.afterDiscount * (discount.value / 100));
      } else if (discount.type === 'value') {
        // Only apply discount if amount is greater then the discount
        if (estimate.total.afterDiscount >= discount.value || estimate.total.shopFee + estimate.total.totalFee >= discount.value) {
          estimate.total.discountedAmount = discount.value;
        }
      }
    }

    if (estimate.isRoLevelFeeEnabled) {
      if (estimate.total.preTaxFee && estimate.total.preTaxFee.length) {
        estimate.total.preTaxFee.forEach((fee, index) => {
          // Tax calculation for shop supplies
          const feePropDiscount = this.calculateOrderItemPropDiscount({
            roundOffTax,
            // total: estimate.total.afterDiscount,
            total: estimate.total.discountApplicableAmount,
            netSubtotal: fee.feeCalcValue,
            discountedAmount: estimate.total.discountedAmount,
          });

          // Calculating shop supplies taxes
          let feeTaxAmount = 0;

          if (fee.taxable) {
            if (estimate.taxValues.taxRates && estimate.taxValues.taxRates.length) {
              const taxData = this.calculateOrderItemTaxes({
                total: fee.feeCalcValue - feePropDiscount,
                taxRates: estimate.taxValues.taxRates,
                roundOffTax,
              });
              feeTaxAmount = taxData.taxAmt;
              estimate.taxValues.taxRates = this.calculateTaxRatesAmount({
                taxRates: estimate.taxValues.taxRates,
                taxAmounts: taxData,
                roundOffTax,
              });
            } else {
              feeTaxAmount = this.calculateOrderItemTax({
                total: fee.feeCalcValue - feePropDiscount,
                taxValue,
                roundOffTax,
              });
            }
          }

          if (fee.taxable) {
            if (roundOffTax) {
              estimate.total.taxedAmount = this.roundOff(estimate.total.taxedAmount + feeTaxAmount);
              estimate.total.taxableAmount = this.roundOff(estimate.total.taxableAmount + estimate.total.preTaxFee[index].feeCalcValue - feePropDiscount);
            } else {
              // estimate.total.taxedAmount += this.roundOff(feeTaxAmount);
              estimate.total.taxedAmount += feeTaxAmount;
              estimate.total.taxableAmount += estimate.total.preTaxFee[index].feeCalcValue - feePropDiscount;
            }
          } else {
            if (roundOffTax) {
              estimate.total.nonTaxableAmount += this.roundOff(estimate.total.preTaxFee[index].feeCalcValue - feePropDiscount);
            } else {
              estimate.total.nonTaxableAmount += estimate.total.preTaxFee[index].feeCalcValue - feePropDiscount;
            }
          }
        });
      }
    } else {
      // Tax calculation for shop supplies
      const shopFeePropDiscount = this.calculateOrderItemPropDiscount({
        roundOffTax,
        // total: estimate.total.afterDiscount,
        total: estimate.total.discountApplicableAmount,
        netSubtotal: estimate.total.shopFee,
        discountedAmount: estimate.total.discountedAmount,
      });
      // Calculating shop supplies taxes
      let shopFeeTaxAmount = 0;

      if (estimate.taxValues.taxRates && estimate.taxValues.taxRates.length) {
        const taxData = this.calculateOrderItemTaxes({
          total: estimate.total.shopFee - shopFeePropDiscount,
          taxRates: estimate.taxValues.taxRates,
          roundOffTax,
        });
        shopFeeTaxAmount = taxData.taxAmt;
        estimate.taxValues.taxRates = this.calculateTaxRatesAmount({
          taxRates: estimate.taxValues.taxRates,
          taxAmounts: taxData,
          roundOffTax,
        });
      } else {
        shopFeeTaxAmount = this.calculateOrderItemTax({
          total: estimate.total.shopFee - shopFeePropDiscount,
          taxValue,
          roundOffTax,
        });
      }

      if (roundOffTax) {
        estimate.total.taxedAmount = this.roundOff(shopFeeTaxAmount);
        estimate.total.taxableAmount = this.roundOff(estimate.total.shopFee - shopFeePropDiscount);
      } else {
        // estimate.total.taxedAmount = this.roundOff(shopFeeTaxAmount);
        estimate.total.taxedAmount = shopFeeTaxAmount;
        estimate.total.taxableAmount = estimate.total.shopFee - shopFeePropDiscount;
      }
    }

    // Service Tax calculation
    included.forEach((e) => {
      // Total for labor, part, other
      e.items.forEach((t) => {
        // if (t.type && !['other', 'sublet'].includes(t.type) && !t.itemId) return;
        // if (t.type === 'discount') return;

        t.taxAmt = 0;
        if (t.type === 'bundle') {
          t.subItems?.forEach((subItem) => {
            subItem.taxAmt = 0;
            if (subItem.price >= 0) {
              const propDiscount = this.calculateOrderItemPropDiscount({
                roundOffTax,
                // total: estimate.total.afterDiscount,
                total: estimate.total.discountApplicableAmount,
                netSubtotal: subItem.netSubTotal,
                discountedAmount: estimate.total.discountedAmount,
              });

              if (estimate.taxValues[subItem.type] && subItem.isTaxable) {
                if (roundOffTax) {
                  if (subItem.type === 'discount') {
                    e.serviceTotal.serviceTaxableAmt = this.roundOff(e.serviceTotal.serviceTaxableAmt - (subItem.netSubTotal - propDiscount));
                  } else {
                    e.serviceTotal.serviceTaxableAmt = this.roundOff(e.serviceTotal.serviceTaxableAmt + (subItem.netSubTotal - propDiscount));
                  }

                  // Calculating taxes
                  if (estimate.taxValues?.taxRates?.length) {
                    const taxData = this.calculateOrderItemTaxes({
                      total: subItem.netSubTotal - (!subItem.isTaxable && subItem.type === 'discount' ? 0 : propDiscount),
                      taxRates: estimate.taxValues.taxRates,
                      roundOffTax,
                      itemType: subItem.type,
                    });
                    subItem.taxAmt = this.roundOff(taxData.taxAmt + subItem.taxAmt);

                    if (subItem.type === 'discount') {
                      subItem.taxAmt *= -1;
                    }

                    estimate.taxValues.taxRates = this.calculateTaxRatesAmount({
                      taxRates: estimate.taxValues.taxRates,
                      taxAmounts: taxData,
                      roundOffTax,
                    });
                  } else {
                    subItem.taxAmt = this.roundOff(
                      subItem.taxAmt +
                      this.calculateOrderItemTax({
                        total: subItem.netSubTotal - (!subItem.isTaxable && subItem.type === 'discount' ? 0 : propDiscount),
                        taxValue,
                        roundOffTax,
                      }),
                    );
                  }

                  e.serviceTotal.taxAmt = this.roundOff(e.serviceTotal.taxAmt + subItem.taxAmt);
                } else {
                  if (subItem.type === 'discount') {
                    e.serviceTotal.serviceTaxableAmt = e.serviceTotal.serviceTaxableAmt - (subItem.netSubTotal - propDiscount);
                  } else {
                    e.serviceTotal.serviceTaxableAmt = e.serviceTotal.serviceTaxableAmt + (subItem.netSubTotal - propDiscount);
                  }

                  // Calculating tax
                  if (estimate.taxValues.taxRates && estimate.taxValues.taxRates.length) {
                    const taxData = this.calculateOrderItemTaxes({
                      total: subItem.netSubTotal - (!subItem.isTaxable && subItem.type === 'discount' ? 0 : propDiscount),
                      taxRates: estimate.taxValues.taxRates,
                      roundOffTax,
                      itemType: subItem.type,
                    });
                    subItem.taxAmt = taxData.taxAmt + subItem.taxAmt;

                    if (subItem.type === 'discount') {
                      subItem.taxAmt *= -1;
                    }

                    estimate.taxValues.taxRates = this.calculateTaxRatesAmount({
                      taxRates: estimate.taxValues.taxRates,
                      taxAmounts: taxData,
                      roundOffTax,
                    });
                  } else {
                    subItem.taxAmt =
                      subItem.taxAmt +
                      this.calculateOrderItemTax({
                        total: subItem.netSubTotal - (!subItem.isTaxable && subItem.type === 'discount' ? 0 : propDiscount),
                        taxValue,
                        roundOffTax,
                      });
                  }

                  e.serviceTotal.taxAmt = e.serviceTotal.taxAmt + subItem.taxAmt;
                }
              } else {
                if (roundOffTax) {
                  if (subItem.type === 'discount') {
                    e.serviceTotal.serviceNonTaxableAmt = this.roundOff(e.serviceTotal.serviceNonTaxableAmt - (subItem.netSubTotal - propDiscount));
                  } else {
                    if (!t.isPassThroughTax) {
                      e.serviceTotal.serviceNonTaxableAmt = this.roundOff(e.serviceTotal.serviceNonTaxableAmt + (subItem.netSubTotal - propDiscount));
                    }
                  }
                } else {
                  if (subItem.type === 'discount') {
                    e.serviceTotal.serviceNonTaxableAmt = e.serviceTotal.serviceNonTaxableAmt - (subItem.netSubTotal - propDiscount);
                  } else {
                    if (!t.isPassThroughTax) {
                      e.serviceTotal.serviceNonTaxableAmt = e.serviceTotal.serviceNonTaxableAmt + (subItem.netSubTotal - propDiscount);
                    }
                  }
                }
              }
            }
          });
        } else {
          if (t.price >= 0 || t.type === 'discount') {
            const propDiscount = this.calculateOrderItemPropDiscount({
              roundOffTax,
              // total: estimate.total.afterDiscount,
              total: estimate.total.discountApplicableAmount,
              netSubtotal: t.netSubTotal,
              discountedAmount: estimate.total.discountedAmount,
            });

            t.propDiscount = propDiscount;
            if (estimate.taxValues[t.type] && t.isTaxable) {
              if (roundOffTax) {
                if (t.type === 'discount') {
                  e.serviceTotal.serviceTaxableAmt = this.roundOff(e.serviceTotal.serviceTaxableAmt - (t.netSubTotal - propDiscount));
                } else {
                  e.serviceTotal.serviceTaxableAmt = this.roundOff(e.serviceTotal.serviceTaxableAmt + (t.netSubTotal - propDiscount));
                }

                // Calculating taxes
                if (estimate.taxValues.taxRates && estimate.taxValues.taxRates.length) {
                  const taxData = this.calculateOrderItemTaxes({
                    total: t.netSubTotal - (!t.isTaxable && t.type === 'discount' ? 0 : propDiscount),
                    taxRates: estimate.taxValues.taxRates,
                    roundOffTax,
                    itemType: t.type,
                  });

                  t.taxAmt = this.roundOff(taxData.taxAmt + t.taxAmt);

                  if (t.type === 'discount') {
                    t.taxAmt *= -1;
                  }

                  estimate.taxValues.taxRates = this.calculateTaxRatesAmount({
                    taxRates: estimate.taxValues.taxRates,
                    taxAmounts: taxData,
                    roundOffTax,
                  });
                } else {
                  t.taxAmt = this.roundOff(
                    t.taxAmt +
                    this.calculateOrderItemTax({
                      total: t.netSubTotal - (!t.isTaxable && t.type === 'discount' ? 0 : propDiscount),
                      taxValue,
                      roundOffTax,
                    }),
                  );
                }

                e.serviceTotal.taxAmt = this.roundOff(e.serviceTotal.taxAmt + t.taxAmt);
              } else {
                if (t.type === 'discount') {
                  e.serviceTotal.serviceTaxableAmt = e.serviceTotal.serviceTaxableAmt - (t.netSubTotal - propDiscount);
                } else {
                  e.serviceTotal.serviceTaxableAmt = e.serviceTotal.serviceTaxableAmt + (t.netSubTotal - propDiscount);
                }

                // Calculating tax
                if (estimate.taxValues.taxRates && estimate.taxValues.taxRates.length) {
                  const taxData = this.calculateOrderItemTaxes({
                    total: t.netSubTotal - (!t.isTaxable && t.type === 'discount' ? 0 : propDiscount),
                    taxRates: estimate.taxValues.taxRates,
                    roundOffTax,
                    itemType: t.type,
                  });

                  t.taxAmt = taxData.taxAmt + t.taxAmt;

                  if (t.type === 'discount') {
                    t.taxAmt *= -1;
                  }

                  estimate.taxValues.taxRates = this.calculateTaxRatesAmount({
                    taxRates: estimate.taxValues.taxRates,
                    taxAmounts: taxData,
                    roundOffTax,
                  });
                } else {
                  const taxData = this.calculateOrderItemTax({
                    total: t.netSubTotal - (!t.isTaxable && t.type === 'discount' ? 0 : propDiscount),
                    taxValue,
                    roundOffTax,
                  });

                  t.taxAmt = taxData + t.taxAmt;

                  if (t.type === 'discount') {
                    t.taxAmt *= -1;
                  }
                  // t.taxAmt =
                  //   t.taxAmt +
                  //   calculateOrderItemTax({
                  //     total: t.netSubTotal - (!t.isTaxable && t.type === 'discount' ? 0 : propDiscount),
                  //     taxValue,
                  //     roundOffTax,
                  //   });
                }

                e.serviceTotal.taxAmt = e.serviceTotal.taxAmt + t.taxAmt;
              }
            } else {
              if (roundOffTax) {
                if (t.type === 'discount') {
                  e.serviceTotal.serviceNonTaxableAmt = this.roundOff(e.serviceTotal.serviceNonTaxableAmt - (t.netSubTotal - propDiscount));
                } else {
                  if (!t?.isPassThroughTax) {
                    e.serviceTotal.serviceNonTaxableAmt = this.roundOff(e.serviceTotal.serviceNonTaxableAmt + (t.netSubTotal - propDiscount));
                  }
                }
              } else {
                if (t.type === 'discount') {
                  e.serviceTotal.serviceNonTaxableAmt = e.serviceTotal.serviceNonTaxableAmt - (t.netSubTotal - propDiscount);
                } else {
                  if (!t.isPassThroughTax) {
                    e.serviceTotal.serviceNonTaxableAmt = e.serviceTotal.serviceNonTaxableAmt + (t.netSubTotal - propDiscount);
                  }
                }
              }
            }
          }
        }
      });

      if (roundOffTax) {
        estimate.total.taxedAmount = this.roundOff(estimate.total.taxedAmount + e.serviceTotal.taxAmt);
      } else {
        estimate.total.taxedAmount = estimate.total.taxedAmount + e.serviceTotal.taxAmt;
      }

      // Total taxable amount
      if (roundOffTax) {
        estimate.total.taxableAmount = this.roundOff(estimate.total.taxableAmount + e.serviceTotal.serviceTaxableAmt);
      } else {
        estimate.total.taxableAmount = estimate.total.taxableAmount + e.serviceTotal.serviceTaxableAmt;
      }
      estimate.total.nonTaxableAmount = this.roundOff(estimate.total.nonTaxableAmount + e.serviceTotal.serviceNonTaxableAmt);
    });

    // estimate.total.nonTaxableAmount = this.roundOff(estimate.total.nonTaxableAmount - estimate.total.serviceLevelDiscount);
    estimate.total.afterDiscount = this.roundOff(estimate.total.afterDiscount - estimate.total.discountedAmount);

    if (tax && !tax.preDiscountTax) {
      if (roundOffTax) {
        estimate.total.afterTax = this.roundOff(estimate.total.afterDiscount + estimate.total.taxedAmount);
      } else {
        estimate.total.afterTax = this.roundOff(estimate.total.afterDiscount + (estimate.total.taxedAmount || 0)) - estimate.total.serviceLevelDiscount;
        estimate.total.afterDiscount -= estimate.total.serviceLevelDiscount;
      }

      estimate.total.grand = this.roundOff(estimate.total.afterTax) + this.roundOff(estimate.total.passThroughTax || 0);
    }

    if (tax?.taxRates?.length > 0 && tax.taxRates[0].salesTaxRate === 0 && estimate.isCountTaxExemptROAsNonTaxedEnable) {
      estimate.total.nonTaxableAmount = this.roundOff(estimate.total.nonTaxableAmount + estimate.total.taxableAmount);
      estimate.total.taxableAmount = 0;
    }

    return estimate;
  }

  transferStockOrderCalculations(transferOrderObj: any, tax: any) {
    let taxValue = 0;

    transferOrderObj.taxRate = taxValue;

    // PO total object
    transferOrderObj.total = {
      parts: 0,
      tire: 0,
      total: 0,
      afterTax: 0,
      grand: 0,
      paid: transferOrderObj?.total?.paid || 0,
      unpaid: transferOrderObj?.total?.unpaid || 0,
      taxedAmount: 0,
      totalQuantity: 0,
      tax: {
        type: 'percentage',
        value: taxValue,
        qboId: tax && tax.tax && tax.tax.qboId,
        name: tax && tax.tax && tax.tax.name ? tax.tax.name : '',
      },
    };

    transferOrderObj.taxValues = {
      taxRates: (tax?.tax?.taxRates || []).map((rate: any) => {
        rate.taxAmount = 0;
        return rate;
      }),
    };

    let taxableAmount = 0;
    let nonTaxableAmount = 0;

    transferOrderObj.transferOrderGroups.forEach((orderGroup: any) => {
      orderGroup.items.forEach((item: any) => {
        if (!item.type || !item.itemId) {
          return;
        }

        item.cost = this.roundOff(item.cost);
        item.transferQty = this.roundOff(item.transferQty);
        item.subTotal = this.roundOff((item.cost + item.coreCost) * item.transferQty);
        transferOrderObj.total.totalQuantity += 1;

        if (item.type === 'part') {
          transferOrderObj.total.parts += item.subTotal;
        } else {
          transferOrderObj.total.tire += item.subTotal;
        }

        transferOrderObj.total.total += item.subTotal;
      });
    });

    transferOrderObj.total.taxableAmount = taxableAmount;
    transferOrderObj.total.nonTaxableAmount = nonTaxableAmount;
    transferOrderObj.total.taxedAmount = this.roundOff((taxValue / 100) * transferOrderObj.total.taxableAmount);

    transferOrderObj.total.afterTax = this.roundOff(transferOrderObj.total.total + transferOrderObj.total.taxedAmount);

    transferOrderObj.total.grand = this.roundOff(transferOrderObj.total.afterTax);

    if (transferOrderObj.taxValues.taxRates && transferOrderObj.taxValues.taxRates.length > 0) {
      transferOrderObj.taxValues.taxRates.forEach((taxItem: any) => {
        taxItem.taxAmount = this.roundOff((taxItem.salesTaxRate / 100) * transferOrderObj.total.taxableAmount);
      });
    }

    return transferOrderObj;
  }

  purchaseOrderCalculations(purchaseOrder: any, tax: any) {
    let taxValue = 0;

    if (tax?.tax?.value) {
      taxValue = tax.tax.value;
    } else if (tax?.tax?.salesTaxRate) {
      taxValue = tax.tax.salesTaxRate;
    }

    purchaseOrder.taxRate = taxValue;
    // PO total object
    purchaseOrder.total = {
      coreCost: 0,
      parts: 0,
      tire: 0,
      total: 0,
      afterTax: 0,
      grand: 0,
      paid: purchaseOrder?.total?.paid || 0,
      unpaid: purchaseOrder?.total?.unpaid || 0,
      taxedAmount: 0,
      totalQuantity: 0,
      tax: {
        type: 'percentage',
        value: taxValue,
        qboId: tax?.tax?.qboId,
        name: tax?.tax?.name ? tax?.tax?.name : '',
      },
    };

    purchaseOrder.taxValues = {
      taxRates: (tax.tax.taxRates || []).map((rate: any) => {
        rate.taxAmount = 0;
        return rate;
      }),
    };

    let taxableAmount = 0;
    let nonTaxableAmount = 0;

    purchaseOrder.items.forEach((item: any) => {
      if (!item.type || !item.itemId) {
        return;
      }
      item.coreCost = this.roundOff(item.core);
      item.cost = this.roundOff(item.cost);
      item.quantity = this.roundOff(item.quantity);

      if (purchaseOrder.excludeCoreCostInPOs) {
        item.subTotal = this.roundOff(item.cost * item.quantity);
      } else {
        item.subTotal = this.roundOff((item.cost + item.core) * item.quantity);
      }

      purchaseOrder.total.totalQuantity += 1;
      if (item.core) {
        purchaseOrder.total.coreCost += Number(item.coreCost) * Number(item.quantity);
      }
      if (item.type === 'part') {
        purchaseOrder.total.parts += item.subTotal;

        if (item.isTaxable) {
          taxableAmount += item.subTotal;
        }
      } else {
        purchaseOrder.total.tire += item.subTotal;

        if (item.isTaxable) {
          taxableAmount += item.subTotal;
        }
      }

      purchaseOrder.total.total += item.subTotal;
    });

    nonTaxableAmount = purchaseOrder.total.total - taxableAmount;
    purchaseOrder.total.taxableAmount = taxableAmount;
    purchaseOrder.total.nonTaxableAmount = nonTaxableAmount;

    if (purchaseOrder?.taxValues?.taxRates?.length > 0) {
      purchaseOrder.taxValues.taxRates.forEach((taxItem: any) => {
        taxItem.taxAmount = this.roundOff((taxItem.salesTaxRate / 100) * purchaseOrder.total.taxableAmount);
        purchaseOrder.total.taxedAmount += this.roundOff(taxItem.taxAmount);
      });
    } else {
      purchaseOrder.total.taxedAmount = this.roundOff((taxValue / 100) * purchaseOrder.total.taxableAmount);
    }
    purchaseOrder.total.afterTax = this.roundOff(purchaseOrder.total.total + purchaseOrder.total.taxedAmount);

    purchaseOrder.total.grand = this.roundOff(purchaseOrder.total.afterTax);

    purchaseOrder.total.afterTax = this.roundOff(purchaseOrder.total.total + purchaseOrder.total.taxedAmount);
    purchaseOrder.total.grand = this.roundOff(purchaseOrder.total.afterTax);

    return purchaseOrder;
  }

  purchaseOrderIncludeExludeCore(purchaseOrder) {
    purchaseOrder = { ...purchaseOrder };
    purchaseOrder.total = {
      ...purchaseOrder.total,
      // coreCost: 0,
      parts: 0,
      tire: 0,
      total: 0,
      grand: 0,
    };

    let taxableAmount = 0;
    let nonTaxableAmount = 0;

    purchaseOrder.items.forEach((item: any) => {
      if (!item.type || !item.itemId) {
        return;
      }

      item.cost = this.roundOff(item.cost);
      item.quantity = this.roundOff(item.quantity);
      item.core = this.roundOff(item.core);

      if (purchaseOrder.excludeCoreCostInPOs) {
        item.subTotal = this.roundOff(item.cost * item.quantity);
      } else {
        item.subTotal = this.roundOff((item.cost + item.core) * item.quantity);
      }

      purchaseOrder.total.totalQuantity += 1;

      if (item.type === 'part') {
        purchaseOrder.total.parts += item.subTotal;

        if (item.isTaxable) {
          taxableAmount += item.subTotal;
        }
      } else {
        purchaseOrder.total.tire += item.subTotal;
        if (item.isTaxable) {
          taxableAmount += item.subTotal;
        }
      }

      purchaseOrder.total.total += item.subTotal;
    });

    nonTaxableAmount = purchaseOrder.total.total - taxableAmount;
    purchaseOrder.total.taxableAmount = taxableAmount;
    purchaseOrder.total.nonTaxableAmount = nonTaxableAmount;
    purchaseOrder.total.taxedAmount = this.roundOff((purchaseOrder.taxRate / 100) * purchaseOrder.total.taxableAmount);

    purchaseOrder.total.afterTax = this.roundOff(purchaseOrder.total.total + purchaseOrder.total.taxedAmount);

    purchaseOrder.total.grand = this.roundOff(purchaseOrder.total.afterTax);
    return purchaseOrder;
  }

  manualOrderCalculation(purchaseOrder: any, tax: any, cancelledItemAmount = 0) {
    let taxValue = 0;

    if (tax && tax.tax) {
      if (tax.tax.value) {
        taxValue = tax.tax.value;
      } else if (tax.tax.salesTaxRate) {
        taxValue = tax.tax.salesTaxRate;
      }
    }

    purchaseOrder.taxRate = taxValue;

    // PO total object
    purchaseOrder.total = {
      coreCost: 0,
      parts: 0,
      tire: 0,
      fee: 0,
      discount: 0,
      total: 0,
      afterTax: 0,
      grand: 0,
      taxedAmount: 0,
      totalQuantity: 0,
      tax: {
        type: 'percentage',
        value: taxValue,
        qboId: tax && tax.tax && tax.tax.qboId,
        name: tax && tax.tax && tax.tax.name ? tax.tax.name : '',
        id: tax?.tax?.id,
      },
    };

    let taxableAmount = 0;
    let nonTaxableAmount = 0;

    purchaseOrder.orders.forEach((order: any) => {
      if (order.items && order.items.length) {
        order.items.forEach((item: any) => {
          let total = 0;
          item.cost = this.roundOff(item.cost);
          item.coreCost = this.roundOff(item.coreCost);
          item.qtyOrdered = this.roundOff(item.qtyOrdered);
          item.quantity = this.roundOff(item.quantity);

          if (purchaseOrder.excludeCoreCostInPOs) {
            total = this.roundOff(item.cost * item.qtyOrdered);
          } else {
            //As per BE devs named - WA in case of purchase online order core cost is already included in the item cost. so should not be added twice
            // if(purchaseOrder.orderingType === 'purchaseOrder') {
            //   total = this.roundOff(item.cost * item.qtyOrdered);
            // } else {
            //   total = this.roundOff((item.cost + item.coreCost) * item.qtyOrdered);
            // }
            total = this.roundOff((item.cost + item.coreCost) * item.qtyOrdered);
          }

          item.totalCost = total;
          item.subTotal = total;

          purchaseOrder.total.totalQuantity += 1;
          if (item.coreCost) {
            purchaseOrder.total.coreCost += Number(item.coreCost) * Number(item.qtyOrdered);
          }

          if (item.type === 'part') {
            purchaseOrder.total.parts += item.subTotal;

            if (item.isTaxable && item.defaultItemStatus !== 'Cancelled') {
              taxableAmount += item.subTotal;
            }
          } else if (item.type === 'hazmatFee') {
            purchaseOrder.total.fee += item.subTotal;

            if (item.isTaxable && item.defaultItemStatus !== 'Cancelled') {
              taxableAmount += item.subTotal;
            }
          } else if (item.type === 'discount') {
            purchaseOrder.total.discount += item.price;

            if (item.isTaxable && item.defaultItemStatus !== 'Cancelled') {
              taxableAmount += item.subTotal;
            }
          } else {
            purchaseOrder.total.tire += item.subTotal;

            if (item.isTaxable && item.defaultItemStatus !== 'Cancelled') {
              taxableAmount += item.subTotal;
            }
          }

          purchaseOrder.total.total += item.subTotal;
        });
      }
    });
    // purchaseOrder.total.total = purchaseOrder.total.total - cancelItemAmout;
    // purchaseOrder.total.grand = purchaseOrder.total.grand - cancelItemAmout;
    nonTaxableAmount = purchaseOrder.total.total - taxableAmount;
    purchaseOrder.total.taxableAmount = taxableAmount;
    purchaseOrder.total.nonTaxableAmount = nonTaxableAmount;
    purchaseOrder.total.taxedAmount = this.roundOff((taxValue / 100) * purchaseOrder.total.taxableAmount);

    purchaseOrder.total.afterTax = this.roundOff(purchaseOrder.total.total + purchaseOrder.total.taxedAmount);

    purchaseOrder.total.grand = this.roundOff(purchaseOrder.total.afterTax);

    if (purchaseOrder.taxValues && purchaseOrder.taxValues.taxRates && purchaseOrder.taxValues.taxRates.length > 0) {
      purchaseOrder.taxValues.taxRates.forEach((taxItem: any) => {
        taxItem.taxAmount = this.roundOff((taxItem.salesTaxRate / 100) * purchaseOrder.total.taxableAmount);
      });
    }

    return purchaseOrder;
  }

  roundOffToDecimalPlaces(number = 0, decimalPlaces = 2) {
    const num = Number(number);
    if (num && !isNaN(num)) {
      return Number(num.toFixed(decimalPlaces));
    }
    return 0;
  }

  toFixed(num, decimalPlaces = 2) {
    return num.toFixed(decimalPlaces);
  }

  /**
   * Round off number to the 2 decimal places
   *
   * if last decimal digit is 0, 1, 2, 3, 4 it drops the digit
   * if last decimal digit is 5, 6, 7, 8, 9 it adds 1 to it
   *
   * @param value - any number
   */
  roundOff(value: number, roundOffDigit = 2) {
    let num = Number(value);
    let isNegativeValue = false;

    if (num < 0) {
      isNegativeValue = true;
      num = num * -1;
    }

    if (!isNaN(num)) {
      // const factor = Math.pow(10, roundOffDigit);
      // num = Math.round(num * factor + Number.EPSILON) / factor;

      // Normalize the number first using toFixed to remove floating point garbage
      const strValue = Number(num.toFixed(roundOffDigit + 5)).toString();
      const [intPart, fracPart = ''] = strValue.split('.');
      const padded = (fracPart + '0'.repeat(roundOffDigit + 1)).slice(0, roundOffDigit + 1);
      const target = padded.slice(0, roundOffDigit);
      const nextDigit = parseInt(padded[roundOffDigit], 10);

      let result = Number(`${intPart}.${target}`);
      if (nextDigit >= 5) {
        result += 1 / Math.pow(10, roundOffDigit);
        result = Number(result.toFixed(roundOffDigit));
      }

      num = result;

      if (isNegativeValue) {
        num = num * -1;
      }

      return num;
    }

    return 0;
    Number.EPSILON; // 2.220446049250313e-16
  }

  calculateMargin(price: number, cost: number) {
    const margin = Math.round(((price - cost) / price) * 100);
    if (isNaN(margin)) {
      return `0%`;
    } else {
      return `${margin}%`;
    }
  }

  calculateTechRate(laborCost: number, hoursBilled: number) {
    const rate = laborCost / hoursBilled;
    if (isNaN(rate) || !isFinite(rate)) {
      return 0;
    } else {
      return rate;
    }
  }

  calculateTechEfficiency(hoursBilled: number, hoursWorked: number) {
    const efficiency = hoursWorked ? (hoursBilled / hoursWorked) * 100 : 0;
    return `${Math.round(efficiency)}%`;
  }

  /**
   * Calculate labor Summary
   * @param estimate estimate object
   */
  calculateLaborSummary(estimate: any) {
    try {
      const laborSummaryData = {
        hoursBilled: 0,
        hoursPlanned: 0,
        hoursWorked: 0,
        inprogress: false,
        inprogressServices: [],
        hoursWorkedtoolTipHtml: '',
        allServicestoolTipHtml: '',
      };

      let allServiceLines = '';
      let allRows = '';

      // Todo calculations
      estimate.services.forEach((service: any) => {
        // if (!service.includeInInvoice) return;

        // calculate planned hours
        service.hoursPlanned = 0;
        //if (service.serviceStatus === 'declined' || service.serviceStatus === 'deferred' || service.serviceStatus === 'deferredLocked') {
        if (!service.includeInInvoice) {
          return;
        }
        service.items.forEach((item: any) => {
          if (item.type === 'labor') {
            service.hoursPlanned += Number.isNaN(Number(item.costEstimatedHours)) ? 0 : Number(item.costEstimatedHours);
          } else if (item.type === 'bundle' && item.subItems && item.subItems.length) {
            item.subItems.forEach((subItem: any) => {
              if (subItem.type === 'labor') {
                service.hoursPlanned += Number.isNaN(Number(subItem.costEstimatedHours)) ? 0 : Number(subItem.costEstimatedHours);
              }
            });
          }
        });

        laborSummaryData.hoursBilled += service.billableHours;
        laborSummaryData.hoursWorked += service.workedHours;
        laborSummaryData.hoursPlanned += service.hoursPlanned;

        allServiceLines += `
        <div class="tooltip-secondary-row seondary-row-padding font-12 font-gray-35 d-flex labor-summary-wrapper mb-0">
          <div class="labor-summary-icon">
            <i class="fal fa-file-alt"></i>
          </div>

          <div class="labor-service-name text-truncate">
            ${service.title}
          </div>

          <div class="labor-hour-billed">
            ${service.billableHours}
          </div>

          <div class="labor-hour-planned">
            ${this.roundOff(service.hoursPlanned)}
          </div>

          <div class="labor-hour-worked">
            ${service.workedHours}
          </div>
        </div>`;

        if (service.status === 'inprogress') {
          laborSummaryData.inprogress = true;
          laborSummaryData.inprogressServices.push(service);
        }
      });

      allRows = `
      <div class="tooltip-2">
        <!-- header -->
        <div class="tooltip-secondary-row seondary-row-padding font-12 font-gray-35 d-flex labor-summary-wrapper header mb-0">
          <div class="labor-summary-icon">
          </div>

          <div class="labor-service-name text-truncate">
            <strong>
              Service Name
            </strong>
          </div>

          <div class="labor-hour-billed">
            <strong>
              Hours billed
            </strong>
          </div>

          <div class="labor-hour-planned">
            <strong>
              Hours estimated
            </strong>
          </div>

          <div class="labor-hour-worked">
            <strong>
              Hours worked
            </strong>
          </div>
        </div>
        </div>
        ${allServiceLines}
      </div>`;

      laborSummaryData.allServicestoolTipHtml += `
        <div class="tooltip-seondary">
          <div class="tooltip-secondary-row font-14 font-bold font-secondary-800 divider-bottom d-flex justify-content-start">
            <i class="fal fa-file-alt mt-1"></i>
            <span>All Services</span>
          </div>
          ${allRows}
        </div>`;

      // Total Hours Worked Tooltip
      if (laborSummaryData.inprogressServices.length > 0) {
        let serviceLine = ``;
        let body = ``;

        laborSummaryData.inprogressServices.map((service) => {
          let hoursPlanned = 0;
          service.items.map((item) => {
            hoursPlanned += Number.isNaN(Number(item.costEstimatedHours)) ? 0 : Number(item.costEstimatedHours);
          });

          serviceLine += `
          <div class="tooltip-secondary-row seondary-row-padding font-12 font-gray-35 d-flex labor-summary-wrapper mb-0">
            <div class="labor-summary-icon">
              <i class="fal fa-file-alt"></i>
            </div>

            <div class="labor-service-name text-truncate">
              ${service.title}
            </div>

            <div class="labor-hour-billed">
              ${service.billableHours}
            </div>

            <div class="labor-hour-planned">
              ${this.roundOff(hoursPlanned)}
            </div>

            <div class="labor-hour-worked">
              ${service.workedHours}
            </div>
          </div>`;
        });

        body = `
          <div class="tooltip-2">
            <!-- header -->
            <div class="tooltip-secondary-row seondary-row-padding font-12 font-gray-35 d-flex labor-summary-wrapper header mb-0">
              <div class="labor-summary-icon"></div>

              <div class="labor-service-name text-truncate">
                <strong>
                  Service Name
                </strong>
              </div>

              <div class="labor-hour-billed">
                <strong>
                  Hours billed
                </strong>
              </div>

              <div class="labor-hour-planned">
                <strong>
                  Hours estimated
                </strong>
              </div>

              <div class="labor-hour-worked">
                <strong>
                  Hours worked
                </strong>
              </div>
            </div>
          </div>
          ${serviceLine}
        </div>`;

        laborSummaryData.hoursWorkedtoolTipHtml += `
          <div class="tooltip-seondary">
            <div class="tooltip-secondary-row font-14 font-bold font-secondary-800 divider-bottom d-flex justify-content-start">
              <i class="fal fa-info-circle text-dark mt-1"></i>
                <span>Following services are in Progress and their time worked is not included in Total hours worked</span>
            </div>
            ${body}
          </div>`;
      }

      // return result
      return laborSummaryData;
    } catch (err) {
      console.log('error in calculateLaborSummary', err);
      return {};
    }
  }

  /**
   * Calculate profitability
   * @param estimate estimate object
   */
  calculateProfitability(estimate: any) {
    try {
      // reset profitability
      const profitabilityData = {
        partCore: 0,
        partCost: 0,
        partPrice: 0,
        partProfit: 0,
        partMargin: 0,
        partCostInfo: '',
        laborPrice: 0,
        laborCost: 0,
        laborProfit: 0,
        laborMargin: 0,
        tireStoragePrice: 0,
        tireStorageCost: 0,
        tireStorageProfit: 0,
        tireStorageMargin: 0,
        tirePrice: 0,
        tireCore: 0,
        tireCost: 0,
        tireMargin: 0,
        tireProfit: 0,
        tireCostInfo: '',
        hazmatFeeMargin: 0,
        hazmatFeePrice: 0,
        hazmatFeeCost: 0,
        hazmatFeeProfit: 0,
        otherPrice: 0,
        otherCost: 0,
        otherProfit: 0,
        subletPrice: 0,
        subletCost: 0,
        subletProfit: 0,
        totalProfit: 0,
        totalCost: 0,
        otherMargin: 0,
        subletMargin: 0,
        totalMargin: 0,
        totalDiscount: 0,
        shopFee: 0,
        roFees: 0,
        totalTax: 0,
        totalNetTireCost: 0,
        totalNetPartCost: 0,
        serviceLevelDiscount: 0,
      };

      // total tax, discount and item costs
      profitabilityData.shopFee = estimate.total.shopFee || 0;
      profitabilityData.roFees = estimate.total.totalFee || 0;
      profitabilityData.totalTax = estimate.total.taxedAmount || 0;
      profitabilityData.totalDiscount = estimate.total.discountedAmount;

      profitabilityData.partCore = (estimate.totalRetrievedCoreCost && estimate.totalRetrievedCoreCost.part) || 0;
      profitabilityData.tireCore = (estimate.totalRetrievedCoreCost && estimate.totalRetrievedCoreCost.tire) || 0;
      profitabilityData.serviceLevelDiscount = estimate.total.serviceLevelDiscount || 0;
      // calculate line item prices with discounts using service's items array
      // doing this step since we don't have redundant discounted line item prices for each item type
      if (estimate.services && estimate.services.length) {
        estimate.services.forEach((service: any) => {
          if (!service.includeInInvoice) return;

          if (!service.items || !Array.isArray(service.items)) service.items = [];

          service.items.forEach((item: any) => {
            if (!item.subTotal) item.subTotal = 0;

            if (item.type === 'bundle' && item.subItems?.length) {
              item.subItems.forEach((subItem: any) => {
                let discountedPrice = subItem.subTotal;

                if (subItem.discount) {
                  discountedPrice =
                    subItem.subTotal -
                    (subItem?.discount?.type === 'percentage' ? subItem.subTotal * (isFinite(subItem.discount.value / 100) ? subItem.discount.value / 100 : 0) : subItem?.discount?.value || 0);
                }

                if (subItem.type) {
                  profitabilityData[`${subItem.type}Price`] += discountedPrice;
                }

                if (subItem.type !== 'labor') {
                  profitabilityData[`${subItem.type}Cost`] += (subItem.cost || 0) * (subItem.count || 0);
                } else {
                  const factor = item.lineItemType === 'refund' ? -1 : 1;
                  if (service.avgTechCost > 0) {
                    profitabilityData[`${subItem.type}Cost`] += factor * service.avgTechCost * subItem.costEstimatedHours || 0;
                  } else {
                    profitabilityData[`${subItem.type}Cost`] += factor * subItem.cost * subItem.costEstimatedHours || 0;
                  }
                }
              });
            } else {
              const discountedPrice =
                item.subTotal - (item?.discount?.type === 'percentage' ? item.subTotal * (isFinite(item.discount.value / 100) ? item.discount.value / 100 : 0) : item?.discount?.value || 0);

              if (item.type) {
                profitabilityData[`${item.type}Price`] += discountedPrice;
              }

              if (item.type === 'hazmatFee' && item.feeValueType === 'percent') {
                profitabilityData[`${item.type}Cost`] += item.cost || 0;
              } else {
                if (item.type !== 'labor') {
                  profitabilityData[`${item.type}Cost`] += (item.cost || 0) * (item.count || 0);
                } else {
                  const factor = item.lineItemType === 'refund' ? -1 : 1;
                  if (service.avgTechCost > 0) {
                    profitabilityData[`${item.type}Cost`] += factor * service.avgTechCost * item.costEstimatedHours || 0;
                  } else {
                    profitabilityData[`${item.type}Cost`] += factor * item.cost || 0;
                  }
                }
              }
            }
          });
        });
      }

      // profitabilityData.totalNetPartCost = profitabilityData.partCost - profitabilityData.partCore;
      profitabilityData.totalNetPartCost = profitabilityData.partCost;
      // profitabilityData.totalNetTireCost = profitabilityData.tireCost - profitabilityData.tireCore;
      profitabilityData.totalNetTireCost = profitabilityData.tireCost;
      profitabilityData.totalCost =
        profitabilityData.totalNetPartCost +
        profitabilityData.laborCost +
        profitabilityData.totalNetTireCost +
        profitabilityData.tireStorageCost +
        profitabilityData.hazmatFeeCost +
        profitabilityData.otherCost +
        profitabilityData?.subletCost || 0;

      // total discounted price
      const {
        partPrice,
        totalNetPartCost,
        laborPrice,
        laborCost,
        tirePrice,
        totalNetTireCost,
        hazmatFeePrice,
        hazmatFeeCost,
        otherPrice,
        otherCost,
        tireStoragePrice,
        tireStorageCost,
        subletPrice,
        subletCost,
      } = profitabilityData;
      const totalDiscountedPrice = partPrice + laborPrice + tirePrice + hazmatFeePrice + otherPrice + tireStoragePrice + (subletPrice || 0);

      // calculate profits
      profitabilityData.partProfit = profitabilityData.partPrice - profitabilityData.totalNetPartCost;
      profitabilityData.laborProfit = profitabilityData.laborPrice - profitabilityData.laborCost;
      profitabilityData.tireProfit = profitabilityData.tirePrice - profitabilityData.totalNetTireCost;
      profitabilityData.tireStorageProfit = profitabilityData.tireStoragePrice - profitabilityData.tireStorageCost;
      profitabilityData.hazmatFeeProfit = profitabilityData.hazmatFeePrice - profitabilityData.hazmatFeeCost;
      profitabilityData.otherProfit = profitabilityData.otherPrice - profitabilityData.otherCost;
      profitabilityData.subletProfit = (profitabilityData?.subletPrice || 0) - (profitabilityData?.subletCost || 0);

      // calculate margins

      profitabilityData.partMargin = 1 - (partPrice !== 0 ? (isFinite(totalNetPartCost / partPrice) ? totalNetPartCost / partPrice : 0) : 1);
      profitabilityData.laborMargin = 1 - (laborPrice !== 0 ? (isFinite(laborCost / laborPrice) ? laborCost / laborPrice : 0) : 1);
      profitabilityData.tireMargin = 1 - (tirePrice !== 0 ? (isFinite(totalNetTireCost / tirePrice) ? totalNetTireCost / tirePrice : 0) : 1);
      profitabilityData.hazmatFeeMargin = 1 - (hazmatFeePrice !== 0 ? (isFinite(hazmatFeeCost / hazmatFeePrice) ? hazmatFeeCost / hazmatFeePrice : 0) : 1);
      profitabilityData.otherMargin = 1 - (otherPrice !== 0 ? (isFinite(otherCost / otherPrice) ? otherCost / otherPrice : 0) : 1);
      profitabilityData.tireStorageMargin = 1 - (tireStoragePrice !== 0 ? (isFinite(tireStorageCost / tireStoragePrice) ? tireStorageCost / tireStoragePrice : 0) : 1);
      profitabilityData.subletMargin = 1 - (subletPrice !== 0 ? (isFinite(subletCost / subletPrice) ? subletCost / subletPrice : 0) : 1);
      /**
       * REFER TO ALP-2660 for this
       */
      // total profit
      let roPrice = 0;

      if (estimate.isRoLevelFeeEnabled) {
        roPrice = totalDiscountedPrice + profitabilityData.roFees;
      } else {
        roPrice = totalDiscountedPrice + profitabilityData.shopFee;
      }

      // ro margin
      profitabilityData.totalProfit = roPrice - profitabilityData.totalDiscount - profitabilityData.totalCost;

      profitabilityData.totalMargin = isFinite(profitabilityData.totalProfit / (roPrice - profitabilityData.totalDiscount))
        ? profitabilityData.totalProfit / (roPrice - profitabilityData.totalDiscount)
        : 0;
      profitabilityData.totalProfit = profitabilityData.totalProfit - profitabilityData.serviceLevelDiscount;
      return profitabilityData;
    } catch (error) {
      console.error('profitbility', error);
      return {};
    }
  }

  selectTheNearestInterval(intervals: any, value: number, roundUp: boolean = false) {
    const data = JSON.parse(JSON.stringify(intervals)).sort((a: any, b: any) => {
      return Math.abs(a.value - value) - Math.abs(b.value - value);
    });

    if (roundUp && data[1]) {
      return data[1];
    } else {
      return data[0];
    }
  }
}
